import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, RotateCcw, Lock, Unlock, User, Settings, X, Plus, Minus, CheckSquare, Eye, Check, Crown, Users, CheckCircle, XCircle, Clock, StickyNote, ChevronDown, ChevronUp, LayoutGrid, Table2, Shield, Tag, Edit3, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';
import { GAME_CONFIGS, ROLES, RoleType } from '../types';
import FloatingTimer from '../components/FloatingTimer';

interface RoleConfig {
  roleId: RoleType;
  count: number;
}

interface GameOptions {
  enableExcalibur: boolean;
  forcedRound: 'single3' | 'single5' | 'global3';
  enableLadyOfLake: boolean;
}

const ROLE_SETUPS: Record<number, RoleConfig[]> = {
  5: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 1 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
  ],
  6: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 2 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
  ],
  7: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 2 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
    { roleId: 'oberon', count: 1 },
  ],
  8: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 3 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
    { roleId: 'minion', count: 1 },
  ],
  9: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 4 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
    { roleId: 'mordred', count: 1 },
  ],
  10: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 4 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
    { roleId: 'mordred', count: 1 },
    { roleId: 'oberon', count: 1 },
  ],
  11: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 5 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
    { roleId: 'mordred', count: 1 },
    { roleId: 'minion', count: 1 },
  ],
  12: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 5 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
    { roleId: 'mordred', count: 1 },
    { roleId: 'oberon', count: 2 },
  ],
  13: [
    { roleId: 'merlin', count: 1 },
    { roleId: 'percival', count: 1 },
    { roleId: 'loyal', count: 6 },
    { roleId: 'morgana', count: 1 },
    { roleId: 'assassin', count: 1 },
    { roleId: 'mordred', count: 1 },
    { roleId: 'oberon', count: 1 },
    { roleId: 'minion', count: 1 },
  ],
};

interface VoteRecord {
  round: number;
  teamSize: number;
  leader: string;
  team: string[];
  votes: Record<string, 'approve' | 'reject'>;
  result: 'approved' | 'rejected' | null;
  isForceRound?: boolean;
  missionResult?: MissionResult;
  missionFailVotes?: number;
}

type MissionResult = 'pending' | 'success' | 'fail';

interface MissionRecord {
  result: MissionResult;
  failVotes?: number;
}

interface EditingVote {
  index: number;
  vote: VoteRecord;
}

interface RoleConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerCount: number;
  roleConfigs: RoleConfig[];
  onSave: (configs: RoleConfig[]) => void;
}

function RoleConfigModal({ isOpen, onClose, playerCount, roleConfigs, onSave }: RoleConfigModalProps) {
  const [localConfigs, setLocalConfigs] = useState<RoleConfig[]>(roleConfigs);

  useEffect(() => {
    setLocalConfigs(roleConfigs);
  }, [roleConfigs]);

  if (!isOpen) return null;

  const updateRoleCount = (roleId: RoleType, delta: number) => {
    const newConfigs = localConfigs.map((config) => {
      if (config.roleId === roleId) {
        const newCount = Math.max(0, config.count + delta);
        return { ...config, count: newCount };
      }
      return config;
    });
    setLocalConfigs(newConfigs);
  };

  const getTotalRoles = () => {
    return localConfigs.reduce((sum, config) => sum + config.count, 0);
  };

  const handleSave = () => {
    onSave(localConfigs);
    onClose();
  };

  const availableGoodRoles: RoleType[] = ['merlin', 'percival', 'loyal', 'goodLancelot'];
  const availableEvilRoles: RoleType[] = ['mordred', 'morgana', 'assassin', 'minion', 'oberon', 'evilLancelot'];

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-avalon-card rounded-xl p-5 w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">自定义角色配置</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-blue-400 text-sm font-medium">正义阵营</span>
          </div>
          <div className="space-y-2">
            {availableGoodRoles.map((roleId) => {
              const config = localConfigs.find(c => c.roleId === roleId);
              if (!config) return null;
              const role = ROLES[roleId];
              const isZeroCount = config.count === 0;
              return (
                <div
                  key={roleId}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                    isZeroCount
                      ? 'bg-transparent border-blue-500/20'
                      : 'bg-blue-500/20 border-blue-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{role.icon}</span>
                    <div className="text-white text-sm font-medium">
                      {role.name} <span className="text-avalon-gold ml-1">×{config.count}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateRoleCount(roleId, -1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white font-medium w-5 text-center text-sm">{config.count}</span>
                    <button
                      onClick={() => updateRoleCount(roleId, 1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
          </div>
          <div className="space-y-2">
            {availableEvilRoles.map((roleId) => {
              const config = localConfigs.find(c => c.roleId === roleId);
              if (!config) return null;
              const role = ROLES[roleId];
              const isZeroCount = config.count === 0;
              return (
                <div
                  key={roleId}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                    isZeroCount
                      ? 'bg-transparent border-red-500/20'
                      : 'bg-red-500/20 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{role.icon}</span>
                    <div className="text-white text-sm font-medium">
                      {role.name} <span className="text-avalon-gold ml-1">×{config.count}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateRoleCount(roleId, -1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white font-medium w-5 text-center text-sm">{config.count}</span>
                    <button
                      onClick={() => updateRoleCount(roleId, 1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t border-avalon-purple/20">
          <button
            onClick={() => {
              const defaultConfigs = getDefaultRoleConfigs(playerCount);
              setLocalConfigs(defaultConfigs);
            }}
            className="flex-1 py-2 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple transition-colors"
          >
            恢复默认
          </button>
          <button
            onClick={handleSave}
            disabled={getTotalRoles() !== playerCount}
            className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            保存配置
          </button>
        </div>
      </div>
    </div>
  );
}



const getDefaultRoleConfigs = (playerCount: number): RoleConfig[] => {
  return ROLE_SETUPS[playerCount] || [];
};

export default function OfflineGamePage() {
  const navigate = useNavigate();

  const [playerCount, setPlayerCount] = useState<number>(5);
  const [isGameLocked, setIsGameLocked] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showRoleConfigModal, setShowRoleConfigModal] = useState(false);
  const [roleConfigs, setRoleConfigs] = useState<RoleConfig[]>([]);
  const [gameOptions, setGameOptions] = useState<GameOptions>({
    enableExcalibur: false,
    forcedRound: 'single3',
    enableLadyOfLake: false,
  });
  const [showRoleDistributionModal, setShowRoleDistributionModal] = useState(false);
  const [showVoteHelperModal, setShowVoteHelperModal] = useState(false);
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'role-distribution' | 'vote-assistant' | null>(null);
  const [flippedPlayers, setFlippedPlayers] = useState<number[]>([]);
  const [players, setPlayers] = useState<Array<{ id: number; role: RoleType; roleName: string }>>([]);
  const [currentPlayer, setCurrentPlayer] = useState<number>(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [showViewConfirm, setShowViewConfirm] = useState(false);
  const [pendingViewPlayer, setPendingViewPlayer] = useState<number | null>(null);
  const [distributionKey, setDistributionKey] = useState(0);
  
  // 投票辅助工具状态
  const [voteCurrentRound, setVoteCurrentRound] = useState(1);
  const [selectedTeam, setSelectedTeam] = useState<number[]>([]);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [teamVotes, setTeamVotes] = useState<Record<number, 'approve' | 'reject'>>({});
  const [currentVoterIndex, setCurrentVoterIndex] = useState(0);
  const [questResults, setQuestResults] = useState<{ result: 'success' | 'fail' | 'pending'; failVotes?: number; flippedPlayer?: number }[]>([
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
  ]);
  const [gameComplete, setGameComplete] = useState(false);
  const [voteSelection, setVoteSelection] = useState<'approve' | 'reject' | null>(null);
  const [voteButtonPositions, setVoteButtonPositions] = useState<{ left: 'approve' | 'reject'; right: 'approve' | 'reject' } | null>(null);
  const [showVoteConfirmDialog, setShowVoteConfirmDialog] = useState(false);
  
  // 王者之剑扩展功能状态
  const [showExcaliburConfirmDialog, setShowExcaliburConfirmDialog] = useState(false);
  const [pendingTeamVotes, setPendingTeamVotes] = useState<Record<number, 'approve' | 'reject'>>({});
  const [showExcaliburSelectDialog, setShowExcaliburSelectDialog] = useState(false);
  const [pendingFlipPlayer, setPendingFlipPlayer] = useState<number | null>(null);
  const [showExcaliburFinalConfirm, setShowExcaliburFinalConfirm] = useState(false);
  
  // 倒计时和笔记插件状态
  const [showFloatingTimer, setShowFloatingTimer] = useState(false);
  const [isNotePanelOpen, setIsNotePanelOpen] = useState(false);
  const [playerNotes, setPlayerNotes] = useState<{ public: string }>({ public: '' });
  const [activeNoteTab, setActiveNoteTab] = useState<string>('public');
  const [notePosition, setNotePosition] = useState({ x: 20, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const noteDragOffset = useRef({ x: 0, y: 0 });
  const notePanelRef = useRef<HTMLDivElement>(null);
  const [showGameRecord, setShowGameRecord] = useState(false);
  const modeButtonsRef = useRef<HTMLDivElement>(null);
  const roleDistributionRef = useRef<HTMLDivElement>(null);
  
  // 游戏记录状态
  const [grPlayers, setGrPlayers] = useState<string[]>(['1 号', '2 号', '3 号', '4 号', '5 号']);
  const [grCurrentRound, setGrCurrentRound] = useState(1);
  const [grLeaderIndex, setGrLeaderIndex] = useState(0);
  const [grVotes, setGrVotes] = useState<VoteRecord[]>([]);
  const [grCurrentVote, setGrCurrentVote] = useState<VoteRecord | null>(null);
  const [grMissionResults, setGrMissionResults] = useState<MissionRecord[]>([
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
  ]);
  const [grShowLeaderSelector, setGrShowLeaderSelector] = useState(true);
  const [grPlayerRoleMarks, setGrPlayerRoleMarks] = useState<Record<number, RoleType[]>>({});
  const [grShowRoleSelector, setGrShowRoleSelector] = useState<number | null>(null);
  const [grIsVotePanelOpen, setGrIsVotePanelOpen] = useState(false);
  const grVotePanelRef = useRef<HTMLDivElement>(null);
  const [grIsPlayerListCollapsed, setGrIsPlayerListCollapsed] = useState(false);
  const [grVoteViewMode, setGrVoteViewMode] = useState<'card' | 'vertical'>('card');
  const [grEditingVote, setGrEditingVote] = useState<EditingVote | null>(null);
  const [grShowEditVoteModal, setGrShowEditVoteModal] = useState(false);
  const [grSelectedVoteIndex, setGrSelectedVoteIndex] = useState<number | null>(null);
  const [grTempMissionResult, setGrTempMissionResult] = useState<MissionResult>('pending');
  const [grTempMissionFailVotes, setGrTempMissionFailVotes] = useState<number>(1);
  const [grShowMissionResultModal, setGrShowMissionResultModal] = useState(false);
  const [grIsGameLocked, setGrIsGameLocked] = useState(false);
  const [grShowLeaderSelectorModal, setGrShowLeaderSelectorModal] = useState(false);
  const [grShowTeamSelectorModal, setGrShowTeamSelectorModal] = useState(false);
  const [grForceRoundUsedCount, setGrForceRoundUsedCount] = useState(0);
  const [isGuideCollapsed, setIsGuideCollapsed] = useState(true);
  
  const config = GAME_CONFIGS[playerCount];
  
  const grGetRequiredTeamSize = (round: number): number => {
    if (!config || !config.missions) return 3;
    const missionIndex = round - 1;
    return config.missions[missionIndex] || 3;
  };

  useEffect(() => {
    const savedPlayerCount = localStorage.getItem('avalon_offline_game_player_count');
    if (savedPlayerCount) {
      try {
        const count = parseInt(savedPlayerCount, 10);
        if (count >= 5 && count <= 13) {
          setPlayerCount(count);
        }
      } catch {
        // ignore parse error
      }
    }

    const savedLockState = localStorage.getItem('avalon_offline_game_lock_state');
    if (savedLockState) {
      try {
        setIsGameLocked(JSON.parse(savedLockState));
      } catch {
        // ignore parse error
      }
    }

    const savedCollapsedState = localStorage.getItem('avalon_offline_game_collapsed_state');
    if (savedCollapsedState) {
      try {
        setConfigSectionCollapsed(JSON.parse(savedCollapsedState));
      } catch {
        // ignore parse error
      }
    }
    
    const savedConfigs = localStorage.getItem('avalon_offline_game_role_configs');
    if (savedConfigs) {
      try {
        setRoleConfigs(JSON.parse(savedConfigs));
      } catch {
        // ignore parse error
      }
    }
    
    const savedOptions = localStorage.getItem('avalon_offline_game_game_options');
    if (savedOptions) {
      try {
        setGameOptions(JSON.parse(savedOptions));
      } catch {
        setGameOptions({
          enableExcalibur: false,
          forcedRound: 'single3',
          enableLadyOfLake: false,
        });
      }
    }

    // 读取身份发放相关状态
    const savedDistributionState = localStorage.getItem('avalon_offline_game_distribution_state');
    if (savedDistributionState) {
      try {
        const state = JSON.parse(savedDistributionState);
        if (state.players) setPlayers(state.players);
        if (typeof state.flippedPlayers !== 'undefined') setFlippedPlayers(state.flippedPlayers);
        if (typeof state.revealed !== 'undefined') setRevealed(state.revealed);
        if (state.playerCount) setPlayerCount(state.playerCount);
        if (state.distributionKey) setDistributionKey(state.distributionKey);
      } catch {
        // ignore parse error
      }
    }
    
    const savedMode = localStorage.getItem('avalon_offline_game_selected_mode');
    if (savedMode) {
      try {
        const mode = JSON.parse(savedMode);
        if (mode === 'role-distribution' || mode === 'vote-assistant') {
          setSelectedMode(mode);
        }
      } catch {
        // ignore parse error
      }
    }
    
    // 读取投票辅助相关状态
    const savedVoteState = localStorage.getItem('avalon_offline_game_vote_state');
    if (savedVoteState) {
      try {
        const state = JSON.parse(savedVoteState);
        if (typeof state.voteCurrentRound !== 'undefined') setVoteCurrentRound(state.voteCurrentRound);
        if (Array.isArray(state.selectedTeam)) setSelectedTeam(state.selectedTeam);
        if (Array.isArray(state.questResults)) setQuestResults(state.questResults);
        if (state.teamVotes) setTeamVotes(state.teamVotes);
        if (typeof state.currentVoterIndex !== 'undefined') setCurrentVoterIndex(state.currentVoterIndex);
        if (typeof state.gameComplete !== 'undefined') setGameComplete(state.gameComplete);
        if (state.voteSelection) setVoteSelection(state.voteSelection);
        if (state.voteButtonPositions) setVoteButtonPositions(state.voteButtonPositions);
      } catch {
        // ignore parse error
      }
    }
    
    // 读取笔记状态
    const savedNotes = localStorage.getItem('avalon_offline_game_notes');
    if (savedNotes) {
      try {
        const notes = JSON.parse(savedNotes);
        setPlayerNotes(notes);
        if (notes.public) setActiveNoteTab('public');
      } catch {
        // ignore parse error
      }
    }
    
    // 读取游戏记录状态
    const savedGameRecordState = localStorage.getItem('avalon_offline_game_game_record_state');
    if (savedGameRecordState) {
      try {
        const state = JSON.parse(savedGameRecordState);
        if (Array.isArray(state.grPlayers)) setGrPlayers(state.grPlayers);
        if (typeof state.grCurrentRound !== 'undefined') setGrCurrentRound(state.grCurrentRound);
        if (typeof state.grLeaderIndex !== 'undefined') setGrLeaderIndex(state.grLeaderIndex);
        if (Array.isArray(state.grVotes)) setGrVotes(state.grVotes);
        if (Array.isArray(state.grMissionResults)) setGrMissionResults(state.grMissionResults);
        if (typeof state.grShowLeaderSelector !== 'undefined') setGrShowLeaderSelector(state.grShowLeaderSelector);
        if (state.grPlayerRoleMarks) setGrPlayerRoleMarks(state.grPlayerRoleMarks);
        if (typeof state.grIsVotePanelOpen !== 'undefined') setGrIsVotePanelOpen(state.grIsVotePanelOpen);
        if (typeof state.grIsPlayerListCollapsed !== 'undefined') setGrIsPlayerListCollapsed(state.grIsPlayerListCollapsed);
        if (state.grVoteViewMode) setGrVoteViewMode(state.grVoteViewMode);
        if (typeof state.grIsGameLocked !== 'undefined') setGrIsGameLocked(state.grIsGameLocked);
      } catch {
        // ignore parse error
      }
    }
  }, []);

  useEffect(() => {
    if (!roleConfigs.length) return;
    localStorage.setItem('avalon_offline_game_role_configs', JSON.stringify(roleConfigs));
  }, [roleConfigs]);

  useEffect(() => {
    localStorage.setItem('avalon_offline_game_game_options', JSON.stringify(gameOptions));
  }, [gameOptions]);

  useEffect(() => {
    localStorage.setItem('avalon_offline_game_player_count', playerCount.toString());
  }, [playerCount]);

  useEffect(() => {
    localStorage.setItem('avalon_offline_game_lock_state', JSON.stringify(isGameLocked));
  }, [isGameLocked]);

  useEffect(() => {
    localStorage.setItem('avalon_offline_game_collapsed_state', JSON.stringify(configSectionCollapsed));
  }, [configSectionCollapsed]);

  // 保存身份发放相关状态
  useEffect(() => {
    const distributionState = {
      players,
      flippedPlayers,
      revealed,
      playerCount,
      distributionKey,
    };
    localStorage.setItem('avalon_offline_game_distribution_state', JSON.stringify(distributionState));
  }, [players, flippedPlayers, revealed, playerCount, distributionKey]);

  // 保存投票辅助相关状态
  useEffect(() => {
    const voteState = {
      voteCurrentRound,
      selectedTeam,
      questResults,
      teamVotes,
      currentVoterIndex,
      gameComplete,
      voteSelection,
      voteButtonPositions,
    };
    localStorage.setItem('avalon_offline_game_vote_state', JSON.stringify(voteState));
  }, [voteCurrentRound, selectedTeam, questResults, teamVotes, currentVoterIndex, gameComplete, voteSelection, voteButtonPositions]);

  useEffect(() => {
    if (selectedMode) {
      localStorage.setItem('avalon_offline_game_selected_mode', JSON.stringify(selectedMode));
    } else {
      localStorage.removeItem('avalon_offline_game_selected_mode');
    }
  }, [selectedMode]);

  // 保存笔记状态
  useEffect(() => {
    localStorage.setItem('avalon_offline_game_notes', JSON.stringify(playerNotes));
  }, [playerNotes]);
  
  // 同步游戏记录玩家列表与顶部人数设置
  useEffect(() => {
    const newPlayers: string[] = [];
    for (let i = 0; i < playerCount; i++) {
      newPlayers.push(`${i + 1}号`);
    }
    setGrPlayers(newPlayers);
  }, [playerCount]);

  // 保存游戏记录状态
  useEffect(() => {
    const gameRecordState = {
      grPlayers,
      grCurrentRound,
      grLeaderIndex,
      grVotes,
      grMissionResults,
      grShowLeaderSelector,
      grPlayerRoleMarks,
      grIsVotePanelOpen,
      grIsPlayerListCollapsed,
      grVoteViewMode,
      grIsGameLocked,
    };
    localStorage.setItem('avalon_offline_game_game_record_state', JSON.stringify(gameRecordState));
  }, [grPlayers, grCurrentRound, grLeaderIndex, grVotes, grMissionResults, grShowLeaderSelector, grPlayerRoleMarks, grIsVotePanelOpen, grIsPlayerListCollapsed, grVoteViewMode, grIsGameLocked]);

  useEffect(() => {
    if (roleConfigs.length > 0) {
      localStorage.setItem('avalon_offline_game_role_configs', JSON.stringify(roleConfigs));
    }
  }, [roleConfigs]);

  useEffect(() => {
    localStorage.setItem('avalon_offline_game_game_options', JSON.stringify(gameOptions));
  }, [gameOptions]);

  useEffect(() => {
    if (selectedMode === 'role-distribution' && roleConfigs.length > 0 && players.length === 0) {
      const rolePool: RoleType[] = [];
      
      roleConfigs.forEach((config) => {
        for (let i = 0; i < config.count; i++) {
          rolePool.push(config.roleId);
        }
      });

      while (rolePool.length < playerCount) {
        rolePool.push('loyal');
      }

      for (let i = rolePool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rolePool[i], rolePool[j]] = [rolePool[j], rolePool[i]];
      }

      const newPlayers = Array.from({ length: playerCount }, (_, i) => ({
        id: i + 1,
        role: rolePool[i] || 'loyal',
        roleName: ROLES[rolePool[i] || 'loyal'].name,
      }));

      setPlayers(newPlayers);
      setFlippedPlayers([]);
    }
  }, [selectedMode, playerCount, roleConfigs, distributionKey]);

  const updatePlayerCount = (count: number) => {
    if (isGameLocked) return;
    setPlayerCount(count);
    
    const defaultConfigs = getDefaultRoleConfigs(count);
    const allRoleIds: RoleType[] = ['merlin', 'percival', 'loyal', 'goodLancelot', 'mordred', 'morgana', 'assassin', 'minion', 'oberon', 'evilLancelot'];
    
    const completeConfigs = allRoleIds.map(roleId => {
      const existing = defaultConfigs.find(c => c.roleId === roleId);
      return existing || { roleId, count: 0 };
    });
    
    setRoleConfigs(completeConfigs);
    resetGame(true);
  };

  const toggleGameLock = () => {
    setIsGameLocked(!isGameLocked);
  };

  const resetGame = (keepMode = false) => {
    setIsGameLocked(false);
    setFlippedPlayers([]);
    setPlayers([]);
    setCurrentPlayer(1);
    setCardFlipped(false);
    setRevealed(false);
    setShowViewModal(false);
    setShowRevealConfirm(false);
    if (!keepMode) {
      setSelectedMode(null);
      localStorage.removeItem('avalon_offline_game_selected_mode');
    }
    // 清除身份发放状态
    localStorage.removeItem('avalon_offline_game_distribution_state');
    // 清除投票辅助状态
    localStorage.removeItem('avalon_offline_game_vote_state');
    // 清除笔记状态
    localStorage.removeItem('avalon_offline_game_notes');
    setPlayerNotes({ public: '' });
    setActiveNoteTab('public');
    // 重置投票辅助状态
    setVoteCurrentRound(1);
    setSelectedTeam([]);
    setTeamVotes({});
    setCurrentVoterIndex(0);
    setQuestResults([
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
    ]);
    setGameComplete(false);
    setVoteSelection(null);
    setVoteButtonPositions(null);
    setShowVoteModal(false);
  };

  // 投票辅助工具函数
  const voteRequiredTeamSize = config.missions[voteCurrentRound - 1] || config.missions[0];
  const votePlayers = Array.from({ length: playerCount }, (_, i) => i);

  const handleSelectPlayer = (playerIndex: number) => {
    if (selectedTeam.includes(playerIndex)) {
      setSelectedTeam(selectedTeam.filter(p => p !== playerIndex));
    } else if (selectedTeam.length < voteRequiredTeamSize) {
      setSelectedTeam([...selectedTeam, playerIndex].sort((a, b) => a - b));
    }
  };

  const openVoteModal = () => {
    if (selectedTeam.length === voteRequiredTeamSize) {
      setCurrentVoterIndex(0);
      setTeamVotes({});
      setVoteSelection(null);
      const isApproveLeft = Math.random() > 0.5;
      setVoteButtonPositions({
        left: isApproveLeft ? 'approve' : 'reject',
        right: isApproveLeft ? 'reject' : 'approve',
      });
      setShowVoteModal(true);
    }
  };

  const handleVote = (vote: 'approve' | 'reject') => {
    setVoteSelection(vote);
  };

  const confirmVote = () => {
    if (!voteSelection) return;
    setShowVoteConfirmDialog(true);
  };

  const finalizeVote = () => {
    setShowVoteConfirmDialog(false);
    const currentPlayer = selectedTeam[currentVoterIndex];
    const updatedVotes = { ...teamVotes, [currentPlayer]: voteSelection! };
    
    if (currentVoterIndex < selectedTeam.length - 1) {
      setTeamVotes(updatedVotes);
      setCurrentVoterIndex(currentVoterIndex + 1);
      setVoteSelection(null);
      const isApproveLeft = Math.random() > 0.5;
      setVoteButtonPositions({
        left: isApproveLeft ? 'approve' : 'reject',
        right: isApproveLeft ? 'reject' : 'approve',
      });
    } else {
      setPendingTeamVotes(updatedVotes);
      
      if (gameOptions.enableExcalibur) {
        setShowExcaliburConfirmDialog(true);
      } else {
        processQuestResult(updatedVotes, null);
      }
    }
  };

  const processQuestResult = (votes: Record<number, 'approve' | 'reject'>, flipPlayerIdx: number | null) => {
    const allVotes = Object.values(votes);
    let rejectCount = allVotes.filter(v => v === 'reject').length;
    
    if (flipPlayerIdx !== null) {
      const flippedVote = votes[flipPlayerIdx];
      rejectCount += flippedVote === 'approve' ? 1 : -1;
    }
    
    let success = true;
    if (voteCurrentRound === 4) {
      success = rejectCount < 2;
    } else if (playerCount === 5 && voteCurrentRound === 3) {
      success = rejectCount < 2;
    } else {
      success = rejectCount === 0;
    }

    const newResults = [...questResults];
    newResults[voteCurrentRound - 1] = {
      result: success ? 'success' : 'fail',
      failVotes: success ? undefined : rejectCount,
      flippedPlayer: flipPlayerIdx !== null ? flipPlayerIdx + 1 : undefined,
    };
    setQuestResults(newResults);

    const successCount = newResults.filter(r => r.result === 'success').length;
    const failCount = newResults.filter(r => r.result === 'fail').length;

    if (successCount === 3 || failCount === 3) {
      setGameComplete(true);
    } else {
      setVoteCurrentRound(voteCurrentRound + 1);
      setSelectedTeam([]);
    }
    setShowVoteModal(false);
    setVoteButtonPositions(null);
    setPendingTeamVotes({});
  };

  const handleExcaliburDecision = (useExcalibur: boolean) => {
    setShowExcaliburConfirmDialog(false);
    if (useExcalibur) {
      setShowExcaliburSelectDialog(true);
    } else {
      processQuestResult(pendingTeamVotes, null);
    }
  };

  const handleFlipPlayerSelect = (playerIdx: number) => {
    setPendingFlipPlayer(playerIdx);
    setShowExcaliburSelectDialog(false);
    setShowExcaliburFinalConfirm(true);
  };

  const confirmExcaliburUse = () => {
    setShowExcaliburFinalConfirm(false);
    if (pendingFlipPlayer !== null) {
      processQuestResult(pendingTeamVotes, pendingFlipPlayer);
    }
  };

  const cancelExcaliburUse = () => {
    setShowExcaliburFinalConfirm(false);
    setPendingFlipPlayer(null);
    setShowExcaliburSelectDialog(true);
  };

  // 笔记面板拖拽处理
  const handleNoteMouseDown = (e: React.MouseEvent) => {
    if (notePanelRef.current && (e.target as HTMLElement).closest('.note-drag-handle')) {
      setIsDragging(true);
      const rect = notePanelRef.current.getBoundingClientRect();
      noteDragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const handleNoteMouseMove = (e: MouseEvent) => {
    if (isDragging && notePanelRef.current) {
      const newX = window.innerWidth - (e.clientX - noteDragOffset.current.x) - 340;
      const newY = e.clientY - noteDragOffset.current.y;
      setNotePosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - 340)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 450)),
      });
    }
  };

  const handleNoteMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleNoteMouseMove);
      window.addEventListener('mouseup', handleNoteMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleNoteMouseMove);
      window.removeEventListener('mouseup', handleNoteMouseUp);
    };
  }, [isDragging]);

  const updateNoteContent = (content: string) => {
    setPlayerNotes({
      ...playerNotes,
      [activeNoteTab]: content
    } as any);
  };

  const getCurrentNoteContent = () => {
    if (activeNoteTab === 'public') {
      return playerNotes.public || '';
    }
    return (playerNotes as any)[activeNoteTab] || '';
  };

  const getPlayerTabName = (index: number) => {
    return `${index + 1}号玩家`;
  };

  const resetVoteGame = () => {
    setVoteCurrentRound(1);
    setSelectedTeam([]);
    setQuestResults([
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
    ]);
    setGameComplete(false);
    setShowVoteModal(false);
    setVoteButtonPositions(null);
    setPendingFlipPlayer(null);
    setShowExcaliburFinalConfirm(false);
    localStorage.removeItem('avalon_offline_game_vote_state');
  };

  const saveRoleConfigs = (newConfigs: RoleConfig[]) => {
    setRoleConfigs(newConfigs);
    localStorage.setItem('avalon_offline_game_role_configs', JSON.stringify(newConfigs));
  };

  const updateGameOption = <K extends keyof GameOptions>(key: K, value: GameOptions[K]) => {
    setGameOptions(prev => ({ ...prev, [key]: value }));
  };

  const handleModeToggle = (mode: 'role-distribution' | 'vote-assistant') => {
    if (selectedMode === mode) {
      setSelectedMode(null);
    } else {
      setSelectedMode(mode);
      setShowGameRecord(false);
      // 自动滚动到玩家身份查看区域
      setTimeout(() => {
        roleDistributionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        window.scrollBy(0, -120);
      }, 100);
    }
  };
  
  const handleGameRecordToggle = () => {
    if (showGameRecord) {
      setShowGameRecord(false);
    } else {
      setShowGameRecord(true);
      setSelectedMode(null);
    }
  };
  
  // 游戏记录处理函数
  const grToggleGameLock = () => {
    setGrIsGameLocked(!grIsGameLocked);
  };
  
  const grStartVoting = () => {
    const grConfig = GAME_CONFIGS[playerCount];
    const requiredTeamSize = grConfig.missions[grCurrentRound - 1] || grConfig.missions[0];
    
    const currentRoundVoteCount = grVotes.filter(v => v.round === grCurrentRound).length;
    const isSingleModeForceRound = 
      (gameOptions.forcedRound === 'single3' && currentRoundVoteCount >= 2) ||
      (gameOptions.forcedRound === 'single5' && currentRoundVoteCount >= 4);
    
    if (isSingleModeForceRound) {
      setGrCurrentVote({
        round: grCurrentRound,
        teamSize: requiredTeamSize,
        leader: grPlayers[grLeaderIndex],
        team: [],
        votes: {},
        result: null,
        isForceRound: true,
      });
    } else {
      setGrCurrentVote({
        round: grCurrentRound,
        teamSize: requiredTeamSize,
        leader: grPlayers[grLeaderIndex],
        team: [],
        votes: {},
        result: null,
      });
    }
    
    setGrIsVotePanelOpen(true);
    setTimeout(() => {
      grVotePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  const grToggleVotePanel = () => {
    if (grIsVotePanelOpen) {
      setGrIsVotePanelOpen(false);
      setGrCurrentVote(null);
    } else {
      grStartVoting();
    }
  };
  
  const grToggleTeamMember = (player: string) => {
    if (!grCurrentVote) return;
    const isInTeam = grCurrentVote.team.includes(player);
    const grConfig = GAME_CONFIGS[playerCount];
    const requiredTeamSize = grConfig.missions[grCurrentRound - 1] || grConfig.missions[0];
    
    if (isInTeam) {
      setGrCurrentVote({
        ...grCurrentVote,
        team: grCurrentVote.team.filter((p) => p !== player),
      });
    } else if (grCurrentVote.team.length < requiredTeamSize) {
      setGrCurrentVote({
        ...grCurrentVote,
        team: [...grCurrentVote.team, player],
      });
    }
  };
  
  const grCastVote = (player: string, vote: 'approve' | 'reject') => {
    if (!grCurrentVote) return;
    setGrCurrentVote({
      ...grCurrentVote,
      votes: { ...grCurrentVote.votes, [player]: vote },
    });
  };
  
  const grFinishVoting = () => {
    if (!grCurrentVote) return;
    const approveCount = Object.values(grCurrentVote.votes).filter((v) => v === 'approve').length;
    const rejectCount = Object.values(grCurrentVote.votes).filter((v) => v === 'reject').length;
    const result: 'approved' | 'rejected' = approveCount > rejectCount ? 'approved' : 'rejected';
    const finalVote: VoteRecord = { ...grCurrentVote, result };
    setGrVotes([...grVotes, finalVote]);
    if (result === 'approved') {
      if (grCurrentRound < 5) {
        setGrCurrentRound(grCurrentRound + 1);
      }
    }
    setGrLeaderIndex((grLeaderIndex + 1) % playerCount);
    setGrIsVotePanelOpen(false);
    setGrCurrentVote(null);
  };
  
  const grFinishForceRound = () => {
    if (!grCurrentVote) return;
    const finalVote: VoteRecord = { 
      ...grCurrentVote, 
      result: 'approved',
      isForceRound: true 
    };
    setGrVotes([...grVotes, finalVote]);
    
    if (gameOptions.forcedRound === 'global3') {
      setGrForceRoundUsedCount(prev => prev + 1);
    }
    
    if (grCurrentRound < 5) {
      setGrCurrentRound(grCurrentRound + 1);
    }
    setGrLeaderIndex((grLeaderIndex + 1) % playerCount);
    setGrIsVotePanelOpen(false);
    setGrCurrentVote(null);
  };
  
  const grStartEditVote = (index: number) => {
    setGrEditingVote({ index, vote: { ...grVotes[index] } });
    setGrShowEditVoteModal(true);
  };
  
  const grRecalculateGameStateFromVotes = (votes: VoteRecord[]) => {
    let currentRound = 1;
    const missionResults: MissionRecord[] = [
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
    ];
    
    votes.forEach((vote) => {
      if (vote.result === 'approved') {
        if (vote.round === currentRound) {
          if (vote.missionResult) {
            missionResults[vote.round - 1] = {
              result: vote.missionResult,
              failVotes: vote.missionFailVotes,
            };
          }
          if (vote.round < 5) {
            currentRound = vote.round + 1;
          }
        }
      } else if (vote.result === 'rejected') {
        if (vote.round < currentRound) {
          currentRound = vote.round + 1;
        }
      }
    });
    
    return { currentRound, missionResults };
  };

  const grSaveEditVote = () => {
    if (!grEditingVote) return;
    const newVotes = [...grVotes];
    newVotes[grEditingVote.index] = grEditingVote.vote;
    setGrVotes(newVotes);
    
    const { currentRound, missionResults } = grRecalculateGameStateFromVotes(newVotes);
    setGrCurrentRound(currentRound);
    setGrMissionResults(missionResults);
    
    setGrShowEditVoteModal(false);
    setGrEditingVote(null);
  };
  
  const grGetVoteResultStyle = (vote: VoteRecord) => {
    if (vote.result === 'approved') {
      if (vote.missionResult === 'success') {
        return 'border-l-4 border-l-blue-500';
      } else if (vote.missionResult === 'fail') {
        return 'border-l-4 border-l-red-500';
      } else {
        return 'border-l-4 border-l-blue-500';
      }
    } else {
      return '';
    }
  };
  
  const grGetVerticalHeaderClass = (vote: VoteRecord) => {
    if (vote.result === 'approved') {
      if (vote.missionResult === 'success') {
        return 'bg-blue-500/30 text-blue-100';
      } else if (vote.missionResult === 'fail') {
        return 'bg-red-500/30 text-red-100';
      } else {
        return 'bg-blue-500/20 text-blue-100';
      }
    } else {
      return 'bg-amber-500/20 text-amber-100';
    }
  };
  
  const grTogglePlayerRole = (playerIndex: number, roleId: RoleType) => {
    const currentRoles = grPlayerRoleMarks[playerIndex] || [];
    let newRoles: RoleType[];
    if (currentRoles.includes(roleId)) {
      newRoles = currentRoles.filter(r => r !== roleId);
    } else {
      if (currentRoles.length >= 3) return;
      newRoles = [...currentRoles, roleId];
    }
    const newMarks = { ...grPlayerRoleMarks, [playerIndex]: newRoles };
    setGrPlayerRoleMarks(newMarks);
  };
  
  const grResetGame = () => {
    const grConfig = GAME_CONFIGS[playerCount];
    const missionCount = grConfig?.missions?.length || 5;
    
    setGrPlayers(Array.from({ length: playerCount }, (_, i) => `${i + 1}号`));
    setGrCurrentRound(1);
    setGrLeaderIndex(0);
    setGrVotes([]);
    setGrCurrentVote(null);
    setGrMissionResults(
      Array.from({ length: missionCount }, () => ({ result: 'pending' as const }))
    );
    setGrPlayerRoleMarks({});
    setGrIsVotePanelOpen(false);
    setGrIsPlayerListCollapsed(false);
    setGrIsGameLocked(false);
    setGrShowLeaderSelector(true);
    setGrForceRoundUsedCount(0);
  };
  
  const grOpenMissionResultModal = (voteIndex: number) => {
    const vote = grVotes[voteIndex];
    if (vote.result !== 'approved') return;
    setGrSelectedVoteIndex(voteIndex);
    setGrTempMissionResult(vote.missionResult || 'pending');
    setGrTempMissionFailVotes(vote.missionFailVotes || 1);
    setGrShowMissionResultModal(true);
  };
  
  const grSaveMissionResult = () => {
    if (grSelectedVoteIndex === null) return;
    const newVotes = [...grVotes];
    newVotes[grSelectedVoteIndex] = {
      ...newVotes[grSelectedVoteIndex],
      missionResult: grTempMissionResult,
      missionFailVotes: grTempMissionResult === 'fail' ? grTempMissionFailVotes : undefined,
    };
    setGrVotes(newVotes);
    const vote = newVotes[grSelectedVoteIndex];
    const round = vote.round;
    const newMissionResults = [...grMissionResults];
    newMissionResults[round - 1] = {
      result: grTempMissionResult,
      failVotes: grTempMissionResult === 'fail' ? grTempMissionFailVotes : undefined,
    };
    setGrMissionResults(newMissionResults);
    setGrShowMissionResultModal(false);
    setGrSelectedVoteIndex(null);
  };
  
  const grClearMissionResult = () => {
    if (grSelectedVoteIndex === null) return;
    const newVotes = [...grVotes];
    newVotes[grSelectedVoteIndex] = {
      ...newVotes[grSelectedVoteIndex],
      missionResult: undefined,
      missionFailVotes: undefined,
    };
    setGrVotes(newVotes);
    const vote = newVotes[grSelectedVoteIndex];
    const round = vote.round;
    const newMissionResults = [...grMissionResults];
    newMissionResults[round - 1] = { result: 'pending' };
    setGrMissionResults(newMissionResults);
    setGrShowMissionResultModal(false);
    setGrSelectedVoteIndex(null);
  };
  
  const grGetVoteStats = (vote: VoteRecord) => {
    const approve = Object.values(vote.votes).filter((v) => v === 'approve').length;
    const reject = Object.values(vote.votes).filter((v) => v === 'reject').length;
    return { approve, reject };
  };
  
  const grGetVoteDetails = (vote: VoteRecord) => {
    const approvePlayers: string[] = [];
    const rejectPlayers: string[] = [];
    Object.entries(vote.votes).forEach(([player, v]) => {
      if (v === 'approve') approvePlayers.push(player);
      else rejectPlayers.push(player);
    });
    const sortByNumber = (a: string, b: string) => {
      const numA = parseInt(a.replace(/[^0-9]/g, ''));
      const numB = parseInt(b.replace(/[^0-9]/g, ''));
      return numA - numB;
    };
    approvePlayers.sort(sortByNumber);
    rejectPlayers.sort(sortByNumber);
    return { approvePlayers, rejectPlayers };
  };
  
  const grGetAvailableRoles = (): RoleType[] => {
    if (playerCount === 5) {
      return ['merlin', 'percival', 'loyal', 'morgana', 'assassin'];
    } else if (playerCount <= 10) {
      return ['merlin', 'percival', 'loyal', 'mordred', 'morgana', 'assassin', 'minion', 'oberon'];
    } else {
      return ['merlin', 'percival', 'loyal', 'mordred', 'morgana', 'assassin', 'minion', 'oberon', 'goodLancelot', 'evilLancelot'];
    }
  };

  const handleViewRole = (playerNum: number) => {
    setPendingViewPlayer(playerNum);
    setShowViewConfirm(true);
  };

  const confirmViewRole = () => {
    if (pendingViewPlayer !== null) {
      setCurrentPlayer(pendingViewPlayer);
      setShowViewModal(true);
      setCardFlipped(false);
      setShowViewConfirm(false);
      setPendingViewPlayer(null);
    }
  };

  const handleCardFlip = () => {
    if (!flippedPlayers.includes(currentPlayer)) {
      setFlippedPlayers([...flippedPlayers, currentPlayer]);
    }
    setCardFlipped(true);
  };

  const handleRevealAll = () => {
    setShowRevealConfirm(true);
  };

  const confirmRevealAll = () => {
    setRevealed(true);
    setShowRevealConfirm(false);
    setShowViewModal(false);
  };

  const getPlayerRole = (playerNum: number) => {
    const player = players.find((p) => p.id === playerNum);
    if (!player || !player.role) return null;
    return ROLES[player.role];
  };

  return (
    <ThemedBackground pageName="mission-vote">
      <div className="min-h-screen p-5 pt-[72px] pb-8">
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-2" style={{ backgroundColor: '#1A0F2A', borderBottom: '1px solid #5A4BFF35' }}>
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">线下游戏</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => setShowFloatingTimer(true)}
              className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-gold hover:bg-avalon-purple transition-colors"
              title="发言计时"
            >
              <Clock className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsNotePanelOpen(true)}
              className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-gold hover:bg-avalon-purple transition-colors"
              title="游戏笔记"
            >
              <StickyNote className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowResetConfirm(true)}
              className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-text hover:text-white transition-colors"
              title="重置游戏"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="mb-3 bg-gradient-to-r from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-2.5">
          <button
            onClick={() => setIsGuideCollapsed(!isGuideCollapsed)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0 w-5 h-5 rounded-lg bg-blue-500/30 flex items-center justify-center">
                <Lightbulb className="w-3 h-3 text-blue-400" />
              </div>
              <span className="text-white font-medium text-sm">使用指南</span>
            </div>
            <ChevronDown 
              className={`w-4 h-4 text-avalon-text transition-transform duration-200 ${isGuideCollapsed ? '' : 'rotate-180'}`}
            />
          </button>
          
          {!isGuideCollapsed && (
            <div className="mt-2 pt-2 border-t border-blue-500/20 space-y-2 text-avalon-text text-xs leading-relaxed">
              <div>
                <span className="text-blue-400 font-medium">【操作流程】</span>
                <div className="mt-1 space-y-1">
                  <div>①选择人数→②确认游戏配置→③确定配置并锁定→④勾选并使用身份发放、投票辅助、游戏记录功能→⑤顶部重置按钮重新设置</div>
                </div>
              </div>
              <div>
                <span className="text-amber-400 font-medium">【功能说明】</span>
                <div className="mt-1 space-y-1">
                  <div>①身份发放：辅助线下无牌时发放角色卡牌</div>
                  <div>②投票辅助：辅助线下无牌时任务的暗置投票</div>
                  <div>③游戏记录：便捷记录游戏每轮组队队形和票型</div>
                  <div>④其他功能：倒计时、游戏发言笔记</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!configSectionCollapsed && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-avalon-text text-sm">游戏人数</div>
            {isGameLocked && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/30 text-green-400 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                已锁定
              </span>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2 mb-2">
            {[5, 6, 7, 8, 9].map((count) => (
              <button
                key={count}
                onClick={() => updatePlayerCount(count)}
                disabled={isGameLocked}
                className={`py-2 rounded-lg text-sm font-medium transition-all ${
                  playerCount === count
                    ? 'bg-avalon-gold text-avalon-bg-dark'
                    : 'bg-avalon-card text-white hover:bg-avalon-purple'
                } ${isGameLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {count}人
              </button>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-2 mb-2">
            {[10, 11, 12, 13].map((count) => (
              <button
                key={count}
                onClick={() => updatePlayerCount(count)}
                disabled={isGameLocked}
                className={`py-2 rounded-lg text-sm font-medium transition-all ${
                  playerCount === count
                    ? 'bg-avalon-gold text-avalon-bg-dark'
                    : 'bg-avalon-card text-white hover:bg-avalon-purple'
                } ${isGameLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {count}人
              </button>
            ))}
            <div
              className={`py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                isGameLocked
                  ? 'bg-amber-500/30 text-amber-400'
                  : 'bg-green-500/30 text-green-400'
              }`}
            >
              {isGameLocked ? (
                <>
                  <Unlock className="w-4 h-4" />
                  <span>锁定</span>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <span>解锁</span>
                </>
              )}
            </div>
          </div>

        </div>
        )}

        <div className="avalon-card mb-4 py-2">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <User className="w-4 h-4 text-avalon-gold" />
              {configSectionCollapsed ? '游戏配置' : '角色配置'}
            </h3>
            {!configSectionCollapsed && (
              <button
                onClick={() => setShowRoleConfigModal(true)}
                disabled={isGameLocked}
                className={`px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${
                  isGameLocked
                    ? 'bg-avalon-purple text-white hover:bg-avalon-purple/70'
                    : 'bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <User className="w-3 h-3" />
                自定义配置
              </button>
            )}
            {configSectionCollapsed && (
              <button
                onClick={() => {
                  setConfigSectionCollapsed(false);
                  if (isGameLocked) {
                    setIsGameLocked(false);
                  }
                }}
                className="px-2 py-1 rounded-lg text-xs font-medium bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400 transition-colors"
              >
                展开配置
              </button>
            )}
          </div>

          {!configSectionCollapsed && (
          <div className="space-y-2">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-blue-400 text-sm font-medium">正义阵营</span>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {roleConfigs
                  .filter((c) => ROLES[c.roleId].faction === 'good' && c.count > 0)
                  .map((config) => {
                    const role = ROLES[config.roleId];
                    return (
                      <div
                        key={config.roleId}
                        className="p-1 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center gap-1"
                      >
                        <span className="text-sm">{role.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[11px] font-medium truncate">
                            {role.name} <span className="text-avalon-gold">×{config.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {roleConfigs
                  .filter((c) => ROLES[c.roleId].faction === 'evil' && c.count > 0)
                  .map((config) => {
                    const role = ROLES[config.roleId];
                    return (
                      <div
                        key={config.roleId}
                        className="p-1 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center gap-1"
                      >
                        <span className="text-sm">{role.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[11px] font-medium truncate">
                            {role.name} <span className="text-avalon-gold">×{config.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="pt-1">
              <div className="flex items-center gap-2 mb-1">
                <Settings className="w-3.5 h-3.5 text-avalon-gold" />
                <span className="text-white font-medium text-xs">扩展规则</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30">
                    <span className="text-white text-xs">启用湖中仙女</span>
                    <input
                      type="checkbox"
                      checked={gameOptions.enableLadyOfLake}
                      onChange={(e) => updateGameOption('enableLadyOfLake', e.target.checked)}
                      className="w-3.5 h-3.5 rounded accent-avalon-gold"
                    />
                  </label>
                  <label className="flex-1 flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30">
                    <span className="text-white text-xs">启用王者之剑</span>
                    <input
                      type="checkbox"
                      checked={gameOptions.enableExcalibur}
                      onChange={(e) => updateGameOption('enableExcalibur', e.target.checked)}
                      className="w-3.5 h-3.5 rounded accent-avalon-gold"
                    />
                  </label>
                </div>
                <div className="flex items-center justify-between p-1.5 rounded-lg bg-avalon-bg-dark/30">
                  <span className="text-white text-xs">强制轮</span>
                  <div className="flex rounded-lg bg-avalon-card overflow-hidden border border-avalon-purple/30">
                    <button
                      onClick={() => updateGameOption('forcedRound', 'single3')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        gameOptions.forcedRound === 'single3'
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : 'text-white hover:bg-avalon-purple/50'
                      }`}
                    >
                      单轮 3 次
                    </button>
                    <button
                      onClick={() => updateGameOption('forcedRound', 'single5')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        gameOptions.forcedRound === 'single5'
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : 'text-white hover:bg-avalon-purple/50'
                      }`}
                    >
                      单轮 5 次
                    </button>
                    <button
                      onClick={() => updateGameOption('forcedRound', 'global3')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        gameOptions.forcedRound === 'global3'
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : 'text-white hover:bg-avalon-purple/50'
                      }`}
                    >
                      全局 3 次
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setConfigSectionCollapsed(true);
                if (!isGameLocked) {
                  setIsGameLocked(true);
                }
              }}
              className="w-full py-2 rounded-xl bg-avalon-gold text-avalon-bg-dark font-semibold text-sm hover:bg-yellow-400 transition-colors mt-1 shadow-lg"
            >
              确认配置并锁定
            </button>
          </div>
          )}
        </div>

        <div className="mb-6" ref={modeButtonsRef}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              onClick={() => handleModeToggle('role-distribution')}
              className={`py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg ${
                selectedMode === 'role-distribution'
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-500/20'
                  : 'bg-blue-500/20 text-white hover:bg-blue-500/30 border border-blue-500/30'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                selectedMode === 'role-distribution'
                  ? 'border-white bg-white'
                  : 'border-white/50'
              }`}>
                {selectedMode === 'role-distribution' && <Check className="w-2.5 h-2.5 text-blue-600" />}
              </div>
              <Eye className="w-4 h-4" />
              <span className="text-sm">身份发放</span>
            </button>
            <button
              onClick={() => handleModeToggle('vote-assistant')}
              className={`py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg ${
                selectedMode === 'vote-assistant'
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-purple-500/20'
                  : 'bg-purple-500/20 text-white hover:bg-purple-500/30 border border-purple-500/30'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                selectedMode === 'vote-assistant'
                  ? 'border-white bg-white'
                  : 'border-white/50'
              }`}>
                {selectedMode === 'vote-assistant' && <Check className="w-2.5 h-2.5 text-purple-600" />}
              </div>
              <CheckSquare className="w-4 h-4" />
              <span className="text-sm">投票辅助</span>
            </button>
          </div>
          <button
            onClick={handleGameRecordToggle}
            className={`w-full py-3 rounded-xl font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              showGameRecord
                ? 'bg-green-500 text-white'
                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30'
            }`}
          >
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              showGameRecord
                ? 'border-white bg-white'
                : 'border-green-500/50'
            }`}>
              {showGameRecord && <Check className="w-2.5 h-2.5 text-green-600" />}
            </div>
            <LayoutGrid className="w-4 h-4" />
            <span className="text-sm">游戏记录</span>
          </button>
        </div>
        {selectedMode === 'role-distribution' && (
          <div className="bg-avalon-card rounded-2xl p-4 border border-white/10 mb-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-1">玩家人数</div>
                  <div className="text-2xl font-bold text-avalon-gold">{playerCount}</div>
                </div>
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-1">已查看</div>
                  <div className="text-2xl font-bold text-green-400">{flippedPlayers.length}</div>
                </div>
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-1">未查看</div>
                  <div className="text-2xl font-bold text-white/80">{playerCount - flippedPlayers.length}</div>
                </div>
              </div>

              <h2 className="text-lg font-semibold text-white mb-4">玩家身份查看</h2>
              
              {/* 揭示后显示所有身份 */}
              {revealed ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {Array.from({ length: playerCount }, (_, i) => i + 1).map((num) => {
                    const player = players.find(p => p.id === num);
                    const role = player?.role ? ROLES[player.role] : null;
                    return (
                      <div
                        key={num}
                        className={`aspect-square rounded-2xl flex flex-col items-center justify-center border-2 ${
                          role?.faction === 'good'
                            ? 'border-blue-400/60 bg-blue-500/20'
                            : 'border-red-400/60 bg-red-500/20'
                        }`}
                      >
                        <div className="text-3xl mb-2">{role?.icon || '⚔️'}</div>
                        <div className={`text-sm font-medium mb-2 ${
                          role?.faction === 'good' ? 'text-blue-300' : 'text-red-300'
                        }`}>
                          {player?.roleName || '忠臣'}
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-white/70 text-xs">玩家</span>
                          <span className="text-white font-bold text-xs">{num}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {Array.from({ length: playerCount }, (_, i) => i + 1).map((num) => {
                    const isViewed = flippedPlayers.includes(num);
                    return (
                      <button
                        key={num}
                        onClick={() => handleViewRole(num)}
                        className={`aspect-square rounded-2xl flex flex-col items-center justify-center transition-all border-2 ${
                          isViewed
                            ? 'border-avalon-gold bg-gradient-to-br from-amber-500/20 to-yellow-500/20'
                            : 'border-white/20 bg-avalon-card hover:border-avalon-gold'
                        }`}
                      >
                        <div className="text-white/60 text-xs mb-1">玩家</div>
                        <div className="text-3xl font-bold text-white mb-2">{num}</div>
                        {isViewed ? (
                          <>
                            <div className="text-avalon-gold text-xs mb-1">已查看</div>
                            <div className="text-white/60 text-[10px]">再次查看</div>
                          </>
                        ) : (
                          <div className="text-white/40 text-xs">点击查看</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* 所有身份已查看完毕，显示揭示身份按钮 */}
              {!revealed && flippedPlayers.length === playerCount && (
                <div className="mt-6">
                  <button
                    onClick={handleRevealAll}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold shadow-lg shadow-amber-500/20 hover:scale-105 transition-all"
                  >
                    揭示身份
                  </button>
                </div>
              )}

              {/* 揭示身份后显示重新开始按钮 */}
              {revealed && (
                <div className="mt-6">
                  <button
                    onClick={() => {
                      setDistributionKey(prev => prev + 1);
                      setFlippedPlayers([]);
                      setRevealed(false);
                      setShowViewModal(false);
                      setCurrentPlayer(1);
                      setCardFlipped(false);
                    }}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold shadow-lg shadow-green-500/20 hover:scale-105 transition-all"
                  >
                    重新开始
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 投票辅助工具区域 */}
          {selectedMode === 'vote-assistant' && (
            <div className="space-y-6">
              {/* 任务进程模块 */}
              <div className="avalon-card p-4 rounded-2xl border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-avalon-gold" />
                    任务进程
                  </h3>
                  <span className="text-avalon-text text-xs">点击记录任务结果</span>
                </div>
                <div className="flex justify-between gap-2">
                  {config.missions.map((missionSize, index) => {
                    const round = index + 1;
                    const record = questResults[index];
                    const isProtected = round === 4;
                    return (
                      <button
                        key={round}
                        onClick={() => {}}
                        disabled={true}
                        className={`flex-1 py-2 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all ${
                          record.result === 'success'
                            ? 'bg-blue-500 text-white'
                            : record.result === 'fail'
                            ? 'bg-red-500 text-white'
                            : 'bg-avalon-purple text-white'
                        }`}
                      >
                        <span className="text-[10px] opacity-80">第{round}轮</span>
                        <span className="text-sm font-medium flex items-center gap-1">
                          {missionSize}人
                          {isProtected && (
                            <Shield className="w-3.5 h-3.5 text-avalon-gold" />
                          )}
                        </span>
                        {record.result === 'success' && (
                          <span className="text-[10px] opacity-90">成功</span>
                        )}
                        {record.result === 'fail' && (
                          <span className="text-[10px] opacity-90">{record.failVotes || 1}反对</span>
                        )}
                        {record.flippedPlayer !== undefined && (
                          <span className="text-[9px] bg-purple-500/30 px-1 rounded mt-0.5">
                            {record.flippedPlayer}号被反转
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 游戏结束提示 */}
              {gameComplete && (
                <div className="avalon-card p-4 rounded-2xl bg-gradient-to-r from-avalon-gold to-yellow-500 border border-white/10">
                  <h3 className="text-xl font-bold text-avalon-bg-dark text-center">
                    {questResults.filter(r => r.result === 'success').length === 3 ? '🎉 好人阵营胜利！' : '💀 坏人阵营胜利！'}
                  </h3>
                  <button
                    onClick={resetVoteGame}
                    className="mt-3 w-full py-2 rounded-lg bg-avalon-bg-dark text-avalon-gold font-semibold flex items-center justify-center gap-2 hover:bg-opacity-80 transition-colors"
                  >
                    重新开始
                  </button>
                </div>
              )}

              {!gameComplete && (
                <>
                  {/* 玩家列表 */}
                  <div className="avalon-card p-4 rounded-2xl border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-avalon-gold" />
                        玩家列表
                      </h3>
                      <span className="text-avalon-text text-xs">
                        已选 {selectedTeam.length}/{voteRequiredTeamSize} 人
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {votePlayers.map((playerIndex) => {
                        const isSelected = selectedTeam.includes(playerIndex);
                        return (
                          <button
                            key={playerIndex}
                            onClick={() => handleSelectPlayer(playerIndex)}
                            className={`p-3 rounded-lg flex items-center gap-2 transition-all ${
                              isSelected
                                ? 'bg-blue-500/30 border border-blue-500 text-white'
                                : 'bg-avalon-bg-dark/50 text-white hover:bg-avalon-purple/30'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <span className="text-sm truncate">{playerIndex + 1}号</span>
                            </div>
                            {isSelected && <CheckCircle className="w-4 h-4 ml-auto" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* 开始投票按钮 */}
                  <button
                    onClick={openVoteModal}
                    disabled={selectedTeam.length !== voteRequiredTeamSize}
                    className={`w-full py-3 text-lg font-medium rounded-xl flex items-center justify-center gap-2 transition-all ${
                      selectedTeam.length === voteRequiredTeamSize
                        ? 'bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400'
                        : 'bg-avalon-purple text-avalon-text cursor-not-allowed opacity-50'
                    }`}
                  >
                    <span>开始第 {voteCurrentRound} 轮投票</span>
                  </button>
                </>
              )}
            </div>
          )}

        {showGameRecord && (
          <div className="space-y-4">
            {/* 任务进程 */}
            <div className="bg-avalon-card rounded-2xl p-4 border border-white/10 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">任务进程</h3>
                <span className="text-avalon-text text-xs">点击记录任务结果</span>
              </div>
              <div className="flex justify-between gap-2">
                {GAME_CONFIGS[playerCount].missions.map((missionSize, index) => {
                  const round = index + 1;
                  const record = grMissionResults[index];
                  const isProtected = round === 4;
                  return (
                    <button
                      key={round}
                      onClick={() => grOpenMissionResultModal(grVotes.findIndex(v => v.round === round && v.result === 'approved'))}
                      disabled={!grVotes.some(v => v.round === round && v.result === 'approved')}
                      className={`flex-1 py-2 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all ${
                        record.result === 'success'
                          ? 'bg-blue-500 text-white'
                          : record.result === 'fail'
                          ? 'bg-red-500 text-white'
                          : 'bg-avalon-purple text-white'
                      }`}
                    >
                      <span className="text-[10px] opacity-80">第{round}轮</span>
                      <span className="text-sm font-medium flex items-center gap-1">
                        {missionSize}人
                        {isProtected && (
                          <Shield className="w-3.5 h-3.5 text-avalon-gold" />
                        )}
                      </span>
                      {record.result === 'success' && (
                        <span className="text-[10px] opacity-90">成功</span>
                      )}
                      {record.result === 'fail' && (
                        <span className="text-[10px] opacity-90">{record.failVotes || 1}反对</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 玩家列表 */}
            <div className="bg-avalon-card rounded-2xl p-4 border border-white/10 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-avalon-gold" />
                  玩家列表
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setGrIsPlayerListCollapsed(!grIsPlayerListCollapsed)}
                    className="p-1.5 rounded-lg text-avalon-text hover:text-avalon-gold hover:bg-avalon-purple/50 transition-all duration-300"
                    title={grIsPlayerListCollapsed ? '展开玩家列表' : '收起玩家列表'}
                  >
                    {grIsPlayerListCollapsed ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronUp className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  grIsPlayerListCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-avalon-text text-xs">设置初始队长</span>
                  <button
                    onClick={() => setGrShowLeaderSelector(!grShowLeaderSelector)}
                    className="flex items-center gap-1 text-avalon-gold text-xs hover:text-yellow-400 transition-colors"
                  >
                    {grPlayers[grLeaderIndex]}
                    <ChevronDown className={`w-3 h-3 transition-transform ${grShowLeaderSelector ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {grShowLeaderSelector && (
                  <div className="grid grid-cols-5 gap-2 p-2 bg-avalon-bg-dark/50 rounded-lg mb-4">
                    {grPlayers.map((player, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setGrLeaderIndex(index);
                          setGrShowLeaderSelector(false);
                        }}
                        className={`py-2 rounded text-sm font-medium transition-colors ${
                          index === grLeaderIndex
                            ? 'bg-avalon-gold text-avalon-bg-dark'
                            : 'bg-avalon-card text-white hover:bg-avalon-purple'
                        }`}
                      >
                        {player}
                      </button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                {grPlayers.map((player, index) => {
                  const markedRoles = grPlayerRoleMarks[index] || [];
                  const isLeader = index === grLeaderIndex;
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg flex items-center gap-2 transition-colors relative overflow-hidden ${
                        isLeader
                          ? 'bg-avalon-gold/20 border border-avalon-gold'
                          : 'bg-avalon-bg-dark/50'
                      }`}
                    >
                      {isLeader && (
                        <Crown className="absolute top-1.5 left-1.5 w-3.5 h-3.5 text-avalon-gold opacity-40" />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm truncate ${isLeader ? 'text-avalon-gold font-medium' : 'text-white'}`}>
                          {player}
                        </span>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {markedRoles.length > 0 ? (
                          markedRoles.map((roleId) => (
                            <button
                              key={roleId}
                              onClick={() => setGrShowRoleSelector(index)}
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-sm transition-all ${
                                ROLES[roleId]?.faction === 'good'
                                  ? 'bg-blue-500/30 border border-blue-500/50 text-blue-400 hover:bg-blue-500/50'
                                  : 'bg-red-500/30 border border-red-500/50 text-red-400 hover:bg-red-500/50'
                              }`}
                              title={ROLES[roleId]?.name}
                            >
                              {ROLES[roleId]?.icon}
                            </button>
                          ))
                        ) : (
                          <button
                            onClick={() => setGrShowRoleSelector(index)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all bg-avalon-purple/50 text-avalon-text hover:bg-avalon-purple hover:text-white"
                            title="标记身份"
                          >
                            <Tag className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
              {grIsPlayerListCollapsed && (
                <div className="text-center text-avalon-text text-sm py-2">
                  玩家列表已收起（{grPlayers.length}人）
                </div>
              )}
            </div>

            {/* 投票面板 */}
            <div className="mb-6">
              <button
                onClick={grToggleVotePanel}
                className={`w-full py-2.5 text-lg font-medium rounded-xl flex items-center justify-center gap-2 transition-all ${
                  grIsVotePanelOpen
                    ? 'bg-avalon-purple text-white'
                    : 'bg-gradient-to-r from-avalon-gold to-yellow-500 text-avalon-bg-dark hover:shadow-lg'
                }`}
              >
                {(() => {
                  const currentRoundVoteCount = grVotes.filter(v => v.round === grCurrentRound).length;
                  const voteIndex = currentRoundVoteCount + 1;
                  const baseText = grIsVotePanelOpen ? `收起` : `开始`;
                  return <span>{baseText}第 {grCurrentRound} 轮第{voteIndex}次投票</span>;
                })()}
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${grIsVotePanelOpen ? 'rotate-180' : ''}`} />
              </button>

              {grIsVotePanelOpen && grCurrentVote && (
                <div className="bg-avalon-card rounded-2xl p-4 border border-white/10 mt-4">
                  <h3 className="text-white font-semibold mb-4">
                    选择任务队员 ({grCurrentVote.team.length}/{GAME_CONFIGS[playerCount].missions[grCurrentRound - 1] || GAME_CONFIGS[playerCount].missions[0]})
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {grPlayers.map((player) => {
                      const isSelected = grCurrentVote.team.includes(player);
                      const isLeader = player === grCurrentVote.leader;
                      return (
                        <button
                          key={player}
                          onClick={() => grToggleTeamMember(player)}
                          disabled={!isSelected && grCurrentVote.team.length >= (GAME_CONFIGS[playerCount].missions[grCurrentRound - 1] || GAME_CONFIGS[playerCount].missions[0])}
                          className={`p-3 rounded-lg flex items-center gap-2 transition-all relative overflow-hidden ${
                            isSelected
                              ? 'bg-blue-500/30 border border-blue-500 text-white'
                              : isLeader
                              ? 'bg-avalon-gold/20 border border-avalon-gold text-avalon-gold'
                              : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-avalon-purple/30'
                          } disabled:opacity-50`}
                        >
                          {isLeader && (
                            <Crown className="absolute top-1 left-1 w-3 h-3 opacity-40" />
                          )}
                          <span className="text-sm">{isSelected ? player : player}</span>
                          {isSelected && <CheckCircle className="w-4 h-4 ml-auto" />}
                        </button>
                      );
                    })}
                  </div>

                  {grCurrentVote.team.length === (GAME_CONFIGS[playerCount].missions[grCurrentRound - 1] || GAME_CONFIGS[playerCount].missions[0]) && (
                    <>
                      {!grCurrentVote.isForceRound && (
                        <div className="border-t border-avalon-purple/30 pt-4 mb-4">
                          <h4 className="text-white font-medium mb-2">投票表决</h4>
                          <div className="grid grid-cols-2 gap-3">
                            {grPlayers.map((player) => {
                              const vote = grCurrentVote.votes[player];
                              return (
                                <div
                                  key={player}
                                  className={`p-3 rounded-lg flex items-center justify-between ${
                                    vote === 'approve'
                                      ? 'bg-blue-500/20 border border-blue-500'
                                      : vote === 'reject'
                                      ? 'bg-red-500/20 border border-red-500'
                                      : 'bg-avalon-bg-dark/50'
                                  }`}
                                >
                                  <span className="text-sm text-white">{player}</span>
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => grCastVote(player, 'approve')}
                                      className={`p-1.5 rounded ${
                                        vote === 'approve' ? 'bg-blue-500 text-white' : 'bg-avalon-purple text-avalon-text'
                                      }`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => grCastVote(player, 'reject')}
                                      className={`p-1.5 rounded ${
                                        vote === 'reject' ? 'bg-red-500 text-white' : 'bg-avalon-purple text-avalon-text'
                                      }`}
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        {grCurrentVote.isForceRound ? (
                          <button 
                            onClick={grFinishForceRound} 
                            className="w-full py-2 rounded-lg bg-amber-500/30 border border-amber-500 text-amber-400 font-medium hover:bg-amber-500/50 transition-colors"
                          >
                            确认强制轮组队
                          </button>
                        ) : (
                          <>
                            {gameOptions.forcedRound === 'global3' && (
                              <button 
                                onClick={grFinishForceRound} 
                                disabled={grForceRoundUsedCount >= 3}
                                className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                                  grForceRoundUsedCount >= 3
                                    ? 'bg-gray-500/30 border border-gray-500 text-gray-400 cursor-not-allowed'
                                    : 'bg-amber-500/30 border border-amber-500 text-amber-400 hover:bg-amber-500/50'
                                }`}
                              >
                                强制轮 {grForceRoundUsedCount >= 3 ? '(已用尽)' : `(${3 - grForceRoundUsedCount}次)`}
                              </button>
                            )}
                            <button 
                              onClick={grFinishVoting} 
                              disabled={Object.keys(grCurrentVote.votes).length !== playerCount}
                              className={`flex-1 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                                gameOptions.forcedRound === 'global3'
                                  ? 'bg-gradient-to-r from-avalon-gold to-yellow-500 text-avalon-bg-dark'
                                  : 'bg-gradient-to-r from-avalon-gold to-yellow-500 text-avalon-bg-dark'
                              }`}
                            >
                              确认投票结果
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 投票记录 */}
            {grVotes.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold">投票记录</h3>
                  <div className="flex items-center gap-1 bg-avalon-card rounded-lg p-1">
                    <button
                      onClick={() => setGrVoteViewMode('card')}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
                        grVoteViewMode === 'card'
                          ? 'bg-avalon-purple text-white'
                          : 'text-avalon-text hover:text-white'
                      }`}
                    >
                      <LayoutGrid className="w-4 h-4" />
                      卡片视图
                    </button>
                    <button
                      onClick={() => setGrVoteViewMode('vertical')}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
                        grVoteViewMode === 'vertical'
                          ? 'bg-avalon-purple text-white'
                          : 'text-avalon-text hover:text-white'
                      }`}
                    >
                      <Table2 className="w-4 h-4" />
                      纵向对比
                    </button>
                  </div>
                </div>
                
                {/* 纵向对比视图 */}
                {grVoteViewMode === 'vertical' && (
                  <div className="bg-avalon-card rounded-2xl p-3 border border-white/10 overflow-hidden">
                    <div className="text-white font-medium mb-2 pb-1.5 border-b border-avalon-purple/20 text-sm flex items-center justify-between">
                      <span>投票记录 - 纵向对比视图</span>
                      <span className="text-avalon-text text-xs">显示每次投票尝试</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[280px]">
                        <thead>
                          <tr className="border-b border-avalon-purple/20">
                            <th className="text-left py-1 px-1.5 text-avalon-text text-xs font-medium sticky left-0 bg-avalon-card z-10 min-w-[50px]">
                              玩家
                            </th>
                            {grVotes.map((vote, index) => {
                              const headerClass = grGetVerticalHeaderClass(vote);
                              
                              return (
                                <th key={index} className={`text-center py-1 px-1 text-xs font-medium min-w-[70px] rounded-t-md ${headerClass}`}>
                                  第{vote.round}轮
                                  {grVotes.filter(v => v.round === vote.round).length > 1 && (
                                    <span className="text-[9px] opacity-80">
                                      第{grVotes.filter((v, i) => v.round === vote.round && i <= index).length}次
                                    </span>
                                  )}
                                </th>
                              );
                            })}
                          </tr>
                          <tr className="border-b border-avalon-purple/30 bg-[#2a2236]">
                            <td className="py-0.5 px-1.5 text-avalon-text text-[10px] sticky left-0 bg-[#2a2236] z-10 font-medium">
                              组队
                            </td>
                            {grVotes.map((vote, index) => {
                              const teamNums = vote.team.map(p => p.match(/(\d+)/)?.[1] || p).join(',');
                              return (
                                <td key={index} className="py-0.5 px-1 text-center">
                                  <span className="text-avalon-text text-[9px] whitespace-nowrap">
                                    {teamNums}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {grPlayers.map((player, playerIndex) => (
                            <tr key={playerIndex} className="border-b border-avalon-purple/10 last:border-b-0">
                              <td className="py-1 px-1.5 text-white text-xs sticky left-0 bg-avalon-card z-10 font-medium">
                                {player}
                              </td>
                              {grVotes.map((vote, index) => {
                                const playerVote = vote.votes[player];
                                const isInTeam = vote.team.includes(player);
                                const isLeader = vote.leader === player;
                                
                                let cellContent = '-';
                                let cellClass = 'bg-avalon-bg-dark/30 text-avalon-text';
                                let captainBorder = '';
                                
                                if (playerVote === 'approve') {
                                  cellContent = '✓';
                                  cellClass = 'bg-blue-500/30 text-blue-400';
                                } else if (playerVote === 'reject') {
                                  cellContent = '✗';
                                  cellClass = 'bg-red-500/30 text-red-400';
                                } else if (isLeader && vote.isForceRound) {
                                  if (isInTeam) {
                                    cellContent = '';
                                    cellClass = 'bg-purple-500/20 text-purple-400';
                                    captainBorder = 'border border-yellow-400 shadow-[0_0_4px_rgba(250,204,21,0.3)]';
                                  } else {
                                    cellContent = '-';
                                    cellClass = 'bg-avalon-bg-dark/50 text-avalon-text';
                                    captainBorder = 'border border-yellow-400 shadow-[0_0_4px_rgba(250,204,21,0.3)]';
                                  }
                                } else if (isLeader) {
                                  cellContent = '';
                                  cellClass = 'bg-avalon-bg-dark/50 text-avalon-text';
                                  captainBorder = 'border border-yellow-400 shadow-[0_0_4px_rgba(250,204,21,0.3)]';
                                } else if (isInTeam) {
                                  cellContent = '';
                                  cellClass = 'bg-purple-500/20 text-purple-400';
                                }
                                
                                return (
                                  <td key={index} className="py-1 px-0.5 text-center">
                                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-xs font-bold ${cellClass} ${captainBorder}`}>
                                      {cellContent}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-1.5 pt-1.5 border-t border-avalon-purple/20 flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                      <span className="text-avalon-text">图例:</span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-blue-500/30"></span>
                        <span className="text-avalon-text">支持</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-red-500/30"></span>
                        <span className="text-avalon-text">反对</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded border border-yellow-400 bg-avalon-bg-dark/50"></span>
                        <span className="text-avalon-text">队长</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-purple-500/20"></span>
                        <span className="text-avalon-text">队员</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-avalon-bg-dark/30"></span>
                        <span className="text-avalon-text">未参与</span>
                      </span>
                    </div>
                  </div>
                )}
                
                {/* 卡片视图 */}
                {grVoteViewMode === 'card' && [...grVotes].reverse().map((vote, index) => {
                  const actualIndex = grVotes.length - 1 - index;
                  const stats = grGetVoteStats(vote);
                  const details = grGetVoteDetails(vote);
                  const voteRecordClass = grGetVoteResultStyle(vote);
                  const hasMissionResult = vote.missionResult && vote.missionResult !== 'pending';
                  
                  const currentTaskVotes = grVotes.filter(v => v.round === vote.round);
                  const voteRoundNumber = currentTaskVotes.findIndex(v => v === vote) + 1;
                  
                  return (
                    <div key={actualIndex} className={`bg-avalon-card rounded-2xl p-4 border border-white/10 ${voteRecordClass}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-white font-medium">第{vote.round}轮 第{voteRoundNumber}次 队长{vote.leader}</span>
                          {vote.isForceRound && (
                            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-400 text-[10px]">
                              强制轮
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                              vote.result === 'approved'
                                ? 'bg-blue-500/30 text-blue-400'
                                : 'bg-red-500/30 text-red-400'
                            }`}
                          >
                            {vote.result === 'approved' ? '通过' : '否决'}
                          </span>
                          <button
                            onClick={() => grStartEditVote(actualIndex)}
                            className="p-0.5 rounded hover:bg-avalon-purple/50 text-avalon-text hover:text-white transition-colors"
                            title="编辑投票"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="text-avalon-text text-xs mb-1">
                        队员：{[...vote.team].sort((a, b) => parseInt(a.replace(/[^0-9]/g, '')) - parseInt(b.replace(/[^0-9]/g, ''))).join('、')}
                      </div>
                      
                      {!vote.isForceRound && (
                        <>
                          <div className="flex gap-3 text-xs mb-1">
                            <span className="text-blue-400 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              支持 {stats.approve}
                            </span>
                            <span className="text-red-400 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              反对 {stats.reject}
                            </span>
                          </div>
                          <div className="bg-avalon-bg-dark/50 rounded-lg p-1.5 text-xs">
                            <div className="mb-1">
                              <span className="text-blue-400">支持 ({stats.approve}人): </span>
                              <span className="text-avalon-text">
                                {details.approvePlayers.length > 0 ? details.approvePlayers.join('、') : '无'}
                              </span>
                            </div>
                            <div>
                              <span className="text-red-400">反对 ({stats.reject}人): </span>
                              <span className="text-avalon-text">
                                {details.rejectPlayers.length > 0 ? details.rejectPlayers.join('、') : '无'}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                      
                      {vote.result === 'approved' && (
                        <div className="mt-2 pt-2 border-t border-avalon-purple/20">
                          <button
                            onClick={() => grOpenMissionResultModal(actualIndex)}
                            className={`w-full py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              hasMissionResult
                                ? vote.missionResult === 'success'
                                  ? 'bg-blue-500/30 text-blue-400 border border-blue-500'
                                  : 'bg-red-500/30 text-red-400 border border-red-500'
                                : 'bg-avalon-purple/30 text-avalon-text hover:bg-avalon-purple/50'
                            }`}
                          >
                            {hasMissionResult
                              ? vote.missionResult === 'success'
                                ? '任务成功 ✓'
                                : `任务失败 ✗ (${vote.missionFailVotes || 1}反对)`
                              : '任务结果'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 游戏记录 - 角色标记选择器 */}
        {grShowRoleSelector !== null && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setGrShowRoleSelector(null)}
          >
            <div
              className="bg-avalon-card rounded-xl p-5 w-full max-w-sm max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">
                  标记 {grPlayers[grShowRoleSelector]} 的身份
                </h3>
                <button
                  onClick={() => setGrShowRoleSelector(null)}
                  className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-blue-400 text-sm font-medium">正义阵营</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {grGetAvailableRoles()
                    .filter((roleId) => ROLES[roleId].faction === 'good')
                    .map((roleId) => {
                      const role = ROLES[roleId];
                      const currentRoles = grPlayerRoleMarks[grShowRoleSelector] || [];
                      const isSelected = currentRoles.includes(roleId);
                      return (
                        <button
                          key={roleId}
                          onClick={() => grTogglePlayerRole(grShowRoleSelector, roleId)}
                          className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                            isSelected
                              ? 'bg-blue-500 text-white'
                              : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-blue-500/30 hover:text-white'
                          }`}
                        >
                          <span className="text-lg">{role.icon}</span>
                          <span className="text-xs truncate w-full text-center">{role.name}</span>
                        </button>
                      );
                    })}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {grGetAvailableRoles()
                    .filter((roleId) => ROLES[roleId].faction === 'evil')
                    .map((roleId) => {
                      const role = ROLES[roleId];
                      const currentRoles = grPlayerRoleMarks[grShowRoleSelector] || [];
                      const isSelected = currentRoles.includes(roleId);
                      return (
                        <button
                          key={roleId}
                          onClick={() => grTogglePlayerRole(grShowRoleSelector, roleId)}
                          className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                            isSelected
                              ? 'bg-red-500 text-white'
                              : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30 hover:text-white'
                          }`}
                        >
                          <span className="text-lg">{role.icon}</span>
                          <span className="text-xs truncate w-full text-center">{role.name}</span>
                        </button>
                      );
                    })}
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                {(grPlayerRoleMarks[grShowRoleSelector] || []).length > 0 && (
                  <button
                    onClick={() => {
                      const newMarks = { ...grPlayerRoleMarks };
                      delete newMarks[grShowRoleSelector];
                      setGrPlayerRoleMarks(newMarks);
                    }}
                    className="flex-1 py-2 rounded-lg bg-avalon-purple/50 text-avalon-text hover:bg-avalon-purple hover:text-white transition-colors text-sm"
                  >
                    清除所有标记
                  </button>
                )}
                <button
                  onClick={() => setGrShowRoleSelector(null)}
                  className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-yellow-400 transition-colors text-sm"
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 游戏记录 - 编辑投票模态框 */}
        {grShowEditVoteModal && grEditingVote && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setGrShowEditVoteModal(false)}
          >
            <div
              className="bg-avalon-card rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">
                  编辑第 {grEditingVote.vote.round} 轮投票
                </h3>
                <button
                  onClick={() => setGrShowEditVoteModal(false)}
                  className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mb-4">
                <label className="text-avalon-text text-xs mb-1 block">队长</label>
                <button
                  onClick={() => setGrShowLeaderSelectorModal(true)}
                  className="w-full bg-avalon-bg-dark/50 rounded-lg px-4 py-3 text-left text-white text-sm outline-none focus:ring-2 focus:ring-avalon-gold flex items-center justify-between group"
                >
                  <span>{grEditingVote.vote.leader}</span>
                  <span className="text-avalon-text group-hover:text-white transition-colors">▼</span>
                </button>
              </div>

              <div className="mb-4">
                <label className="text-avalon-text text-xs mb-1 block">
                  任务队员（{grEditingVote.vote.team.length}/{grGetRequiredTeamSize(grEditingVote.vote.round)}）
                </label>
                <button
                  onClick={() => setGrShowTeamSelectorModal(true)}
                  className={`w-full bg-avalon-bg-dark/50 rounded-lg px-4 py-3 text-left text-sm outline-none focus:ring-2 focus:ring-avalon-gold flex items-center justify-between group ${
                    grEditingVote.vote.team.length !== grGetRequiredTeamSize(grEditingVote.vote.round)
                      ? 'text-red-400'
                      : 'text-white'
                  }`}
                >
                  <span>
                    {grEditingVote.vote.team.length === 0
                      ? '请选择队员'
                      : grEditingVote.vote.team.join('、')}
                  </span>
                  <span className="text-avalon-text group-hover:text-white transition-colors">▼</span>
                </button>
                {grEditingVote.vote.team.length !== grGetRequiredTeamSize(grEditingVote.vote.round) && (
                  <p className="text-red-400 text-xs mt-1">
                    请恰好选择 {grGetRequiredTeamSize(grEditingVote.vote.round)} 名队员
                  </p>
                )}
              </div>

              {!grEditingVote.vote.isForceRound && (
                <>
                  <div className="mb-4">
                    <label className="text-avalon-text text-xs mb-1 block">投票结果</label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setGrEditingVote({
                            ...grEditingVote,
                            vote: { ...grEditingVote.vote, result: 'approved' }
                          });
                        }}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          grEditingVote.vote.result === 'approved'
                            ? 'bg-blue-500 text-white'
                            : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-blue-500/30'
                        }`}
                      >
                        通过
                      </button>
                      <button
                        onClick={() => {
                          setGrEditingVote({
                            ...grEditingVote,
                            vote: { ...grEditingVote.vote, result: 'rejected' }
                          });
                        }}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          grEditingVote.vote.result === 'rejected'
                            ? 'bg-red-500 text-white'
                            : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30'
                        }`}
                      >
                        否决
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-avalon-text text-xs mb-1 block">个人投票</label>
                    <div className="space-y-2">
                      {grPlayers.map((playerName) => (
                        <div key={playerName} className="flex items-center justify-between bg-avalon-bg-dark/30 rounded-lg px-3 py-2">
                          <span className="text-white text-sm">{playerName}</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                const newVotes: Record<string, 'approve' | 'reject'> = { 
                                  ...grEditingVote.vote.votes, 
                                  [playerName]: 'approve' 
                                };
                                setGrEditingVote({
                                  ...grEditingVote,
                                  vote: { ...grEditingVote.vote, votes: newVotes }
                                });
                              }}
                              className={`p-1.5 rounded ${
                                grEditingVote.vote.votes[playerName] === 'approve'
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-avalon-purple text-avalon-text'
                              }`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                const newVotes: Record<string, 'approve' | 'reject'> = { 
                                  ...grEditingVote.vote.votes, 
                                  [playerName]: 'reject' 
                                };
                                setGrEditingVote({
                                  ...grEditingVote,
                                  vote: { ...grEditingVote.vote, votes: newVotes }
                                });
                              }}
                              className={`p-1.5 rounded ${
                                grEditingVote.vote.votes[playerName] === 'reject'
                                  ? 'bg-red-500 text-white'
                                  : 'bg-avalon-purple text-avalon-text'
                              }`}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setGrShowEditVoteModal(false)}
                  className="flex-1 py-2 rounded-lg bg-avalon-purple text-avalon-text hover:text-white transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={grSaveEditVote}
                  className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-avalon-gold/90 transition-colors"
                >
                  保存修改
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 游戏记录 - 任务结果模态框 */}
        {grShowMissionResultModal && grSelectedVoteIndex !== null && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setGrShowMissionResultModal(false)}
          >
            <div
              className="bg-avalon-card rounded-xl p-6 w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-white font-semibold text-center mb-6">
                第 {grVotes[grSelectedVoteIndex].round} 轮任务结果
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => setGrTempMissionResult('success')}
                  className={`py-2.5 rounded-lg font-medium transition-colors ${
                    grTempMissionResult === 'success'
                      ? 'bg-blue-500 text-white'
                      : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-blue-500/30'
                  }`}
                >
                  成功
                </button>
                <button
                  onClick={() => setGrTempMissionResult('fail')}
                  className={`py-2.5 rounded-lg font-medium transition-colors ${
                    grTempMissionResult === 'fail'
                      ? 'bg-red-500 text-white'
                      : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30'
                  }`}
                >
                  失败
                </button>
              </div>

              {grTempMissionResult === 'fail' && (
                <div className="mb-6">
                  <label className="text-avalon-text text-xs mb-1 block text-center">
                    失败票数
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {[1, 2, 3, 4, 5].map((num) => (
                      <button
                        key={num}
                        onClick={() => setGrTempMissionFailVotes(num)}
                        className={`py-2 rounded-lg text-lg font-medium transition-colors ${
                          grTempMissionFailVotes === num
                            ? 'bg-red-500 text-white'
                            : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={grClearMissionResult}
                  className="flex-1 py-2 rounded-lg bg-avalon-purple text-avalon-text hover:text-white transition-colors"
                >
                  清除标记
                </button>
                <button
                  onClick={() => setGrShowMissionResultModal(false)}
                  className="flex-1 py-2 rounded-lg bg-avalon-bg-dark/50 text-avalon-text hover:text-white transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={grSaveMissionResult}
                  disabled={grTempMissionResult === 'pending'}
                  className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-avalon-gold/90 transition-colors disabled:opacity-50"
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 游戏记录 - 队长选择弹窗 */}
        {grShowLeaderSelectorModal && grEditingVote && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4"
            onClick={() => setGrShowLeaderSelectorModal(false)}
          >
            <div
              className="bg-avalon-card rounded-xl p-6 w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">选择队长</h3>
                <button
                  onClick={() => setGrShowLeaderSelectorModal(false)}
                  className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {grPlayers.map((player) => (
                  <button
                    key={player}
                    onClick={() => {
                      setGrEditingVote({
                        ...grEditingVote,
                        vote: { ...grEditingVote.vote, leader: player }
                      });
                      setGrShowLeaderSelectorModal(false);
                    }}
                    className={`py-3 rounded-lg font-medium transition-colors ${
                      grEditingVote.vote.leader === player
                        ? 'bg-avalon-gold text-avalon-bg-dark'
                        : 'bg-avalon-purple text-avalon-text hover:bg-avalon-purple/70'
                    }`}
                  >
                    {player}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 游戏记录 - 队员选择弹窗 */}
        {grShowTeamSelectorModal && grEditingVote && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4"
            onClick={() => setGrShowTeamSelectorModal(false)}
          >
            <div
              className="bg-avalon-card rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">
                  选择任务队员（{grEditingVote.vote.team.length}/{grGetRequiredTeamSize(grEditingVote.vote.round)}）
                </h3>
                <button
                  onClick={() => setGrShowTeamSelectorModal(false)}
                  className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-avalon-bg-dark/30 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-avalon-text">已选：{grEditingVote.vote.team.length}人</span>
                  <span className="text-avalon-text">应选：{grGetRequiredTeamSize(grEditingVote.vote.round)}人</span>
                </div>
                {grEditingVote.vote.team.length !== grGetRequiredTeamSize(grEditingVote.vote.round) && (
                  <div className="text-red-400 text-xs mt-1">
                    请恰好选择 {grGetRequiredTeamSize(grEditingVote.vote.round)} 名队员
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3 max-h-64 overflow-y-auto p-2 bg-avalon-bg-dark/30 rounded-lg">
                {grPlayers.map((player) => {
                  const isSelected = grEditingVote.vote.team.includes(player);
                  const canSelect = grEditingVote.vote.team.length < grGetRequiredTeamSize(grEditingVote.vote.round);
                  return (
                    <button
                      key={player}
                      onClick={() => {
                        if (!isSelected && !canSelect) return;
                        const newTeam = isSelected
                          ? grEditingVote.vote.team.filter(p => p !== player)
                          : [...grEditingVote.vote.team, player];
                        setGrEditingVote({
                          ...grEditingVote,
                          vote: { ...grEditingVote.vote, team: newTeam }
                        });
                      }}
                      disabled={!isSelected && !canSelect}
                      className={`py-3 rounded-lg font-medium transition-colors ${
                        isSelected
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : !canSelect
                          ? 'bg-avalon-bg-dark/30 text-avalon-text/30 cursor-not-allowed'
                          : 'bg-avalon-purple text-avalon-text hover:bg-avalon-purple/70'
                      }`}
                    >
                      {player}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setGrShowTeamSelectorModal(false)}
                  className="flex-1 py-2.5 rounded-lg bg-avalon-bg-dark/50 text-avalon-text hover:text-white transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={() => setGrShowTeamSelectorModal(false)}
                  disabled={grEditingVote.vote.team.length !== grGetRequiredTeamSize(grEditingVote.vote.round)}
                  className="flex-1 py-2.5 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-avalon-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 投票弹窗 */}
        {showVoteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/70 backdrop-blur-sm">
            <div className="w-full max-w-xs relative">
              <div className="bg-gradient-to-b from-[#2D1F4E] to-[#1F1535] rounded-2xl p-4 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-center flex-1">
                    <h2 className="text-xl font-bold text-white mb-1">任务投票</h2>
                    <p className="text-white/60 text-xs">第{voteCurrentRound}轮 · 队伍投票</p>
                  </div>
                  <button
                    onClick={() => setShowVoteModal(false)}
                    className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors ml-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-[#1A0F2A]/50 rounded-xl p-3 mb-4">
                  <div className="flex items-center justify-center gap-2 text-amber-400">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">队伍：</span>
                    <div className="flex items-center gap-1.5">
                      {selectedTeam.map((playerIdx) => (
                        <div
                          key={playerIdx}
                          className="w-6 h-6 rounded-full bg-amber-400/20 border border-amber-400/50 flex items-center justify-center text-amber-400 font-semibold text-xs"
                        >
                          {playerIdx + 1}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-[#1A0F2A]/50 rounded-xl p-4 mb-4">
                  <div className="text-center mb-3">
                    <div className="text-white/60 text-xs mb-1">当前投票</div>
                    <div className="text-2xl font-bold text-amber-400 mb-2">{selectedTeam[currentVoterIndex] + 1}号</div>
                    <p className="text-white/50 text-xs">请交给该玩家投票</p>
                  </div>

                  <div className="flex items-center justify-center gap-2 mb-2">
                    {selectedTeam.map((playerIdx, idx) => {
                      const isCurrent = idx === currentVoterIndex;
                      const hasVoted = teamVotes[playerIdx] !== undefined;
                      return (
                        <div
                          key={playerIdx}
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                            isCurrent
                              ? 'bg-amber-400 text-[#1A0F2E]'
                              : hasVoted
                              ? 'bg-amber-500/30 border border-amber-500 text-amber-400'
                              : 'bg-white/5 text-white/30'
                          }`}
                        >
                          {playerIdx + 1}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="text-center mb-3">
                  <p className="text-white/60 text-xs">请选择投票</p>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  {voteButtonPositions && (
                    <>
                      <button
                        onClick={() => handleVote(voteButtonPositions.left)}
                        className={`aspect-[4/3] rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all active:scale-95 relative ${
                          voteSelection === voteButtonPositions.left
                            ? voteButtonPositions.left === 'approve'
                              ? 'bg-gradient-to-br from-[#2B4C8C] to-[#1F3A6B] border-[#4A7BC8]'
                              : 'bg-gradient-to-br from-[#8C2B3C] to-[#6B1F2A] border-[#C84A5A]'
                            : voteButtonPositions.left === 'approve'
                            ? 'bg-gradient-to-br from-[#2B4C8C]/40 to-[#1F3A6B]/40 border-[#4A7BC8]/50'
                            : 'bg-gradient-to-br from-[#8C2B3C]/40 to-[#6B1F2A]/40 border-[#C84A5A]/50'
                        }`}
                      >
                        {voteSelection === voteButtonPositions.left && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                            <CheckCircle className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                        {voteButtonPositions.left === 'approve' ? (
                          <CheckCircle className={`w-8 h-8 ${voteSelection === voteButtonPositions.left ? 'text-[#6FA3E8]' : 'text-white/60'}`} />
                        ) : (
                          <XCircle className={`w-8 h-8 ${voteSelection === voteButtonPositions.left ? 'text-[#E86F7F]' : 'text-white/60'}`} />
                        )}
                        <span className={`text-sm font-bold ${voteSelection === voteButtonPositions.left ? (voteButtonPositions.left === 'approve' ? 'text-[#6FA3E8]' : 'text-[#E86F7F]') : 'text-white/60'}`}>
                          {voteButtonPositions.left === 'approve' ? '成功' : '失败'}
                        </span>
                      </button>
                      <button
                        onClick={() => handleVote(voteButtonPositions.right)}
                        className={`aspect-[4/3] rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-all active:scale-95 relative ${
                          voteSelection === voteButtonPositions.right
                            ? voteButtonPositions.right === 'approve'
                              ? 'bg-gradient-to-br from-[#2B4C8C] to-[#1F3A6B] border-[#4A7BC8]'
                              : 'bg-gradient-to-br from-[#8C2B3C] to-[#6B1F2A] border-[#C84A5A]'
                            : voteButtonPositions.right === 'approve'
                            ? 'bg-gradient-to-br from-[#2B4C8C]/40 to-[#1F3A6B]/40 border-[#4A7BC8]/50'
                            : 'bg-gradient-to-br from-[#8C2B3C]/40 to-[#6B1F2A]/40 border-[#C84A5A]/50'
                        }`}
                      >
                        {voteSelection === voteButtonPositions.right && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                            <CheckCircle className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                        {voteButtonPositions.right === 'approve' ? (
                          <CheckCircle className={`w-8 h-8 ${voteSelection === voteButtonPositions.right ? 'text-[#6FA3E8]' : 'text-white/60'}`} />
                        ) : (
                          <XCircle className={`w-8 h-8 ${voteSelection === voteButtonPositions.right ? 'text-[#E86F7F]' : 'text-white/60'}`} />
                        )}
                        <span className={`text-sm font-bold ${voteSelection === voteButtonPositions.right ? (voteButtonPositions.right === 'approve' ? 'text-[#6FA3E8]' : 'text-[#E86F7F]') : 'text-white/60'}`}>
                          {voteButtonPositions.right === 'approve' ? '成功' : '失败'}
                        </span>
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={confirmVote}
                  disabled={!voteSelection}
                  className={`w-full py-2.5 rounded-xl font-bold text-sm transition-all ${
                    voteSelection
                      ? 'bg-gradient-to-r from-avalon-gold to-yellow-500 text-avalon-bg-dark hover:shadow-lg'
                      : 'bg-avalon-purple text-white/40 cursor-not-allowed'
                  }`}
                >
                  确认投票
                </button>

                {showVoteConfirmDialog && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <div className="bg-avalon-card rounded-2xl p-6 max-w-xs w-full border border-white/10">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                          <CheckSquare className="w-8 h-8 text-amber-400" strokeWidth={1.5} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">确认投票选择？</h3>
                        <p className="text-white/60 text-sm">
                          第{voteCurrentRound}轮 · {selectedTeam[currentVoterIndex] + 1}号玩家
                        </p>
                        <p className={`text-sm font-semibold mt-2 ${
                          voteSelection === 'approve' ? 'text-blue-400' : 'text-red-400'
                        }`}>
                          投票结果：{voteSelection === 'approve' ? '✓ 成功' : '✗ 失败'}
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowVoteConfirmDialog(false)}
                          className="flex-1 py-3 rounded-xl bg-avalon-card text-white border border-white/20 hover:bg-opacity-80 transition-colors"
                        >
                          返回修改
                        </button>
                        <button
                          onClick={finalizeVote}
                          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-avalon-gold to-yellow-500 text-white font-semibold shadow-lg shadow-amber-500/20 hover:scale-105 transition-all"
                        >
                          确认提交
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showExcaliburConfirmDialog && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <div className="bg-avalon-card rounded-2xl p-6 max-w-xs w-full border border-white/10">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                          <Crown className="w-8 h-8 text-purple-400" strokeWidth={1.5} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">是否使用王者之剑？</h3>
                        <p className="text-white/60 text-sm mb-3">
                          第{voteCurrentRound}轮投票已完成
                        </p>
                        <p className="text-white/40 text-xs">
                          队长可以选择一名队员，暗中反转其投票结果
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleExcaliburDecision(false)}
                          className="flex-1 py-3 rounded-xl bg-avalon-card text-white border border-white/20 hover:bg-opacity-80 transition-colors"
                        >
                          不使用
                        </button>
                        <button
                          onClick={() => handleExcaliburDecision(true)}
                          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold shadow-lg shadow-purple-500/20 hover:scale-105 transition-all"
                        >
                          使用
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showExcaliburSelectDialog && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <div className="bg-avalon-card rounded-2xl p-6 max-w-xs w-full border border-white/10">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                          <Users className="w-8 h-8 text-purple-400" strokeWidth={1.5} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">选择反转目标</h3>
                        <p className="text-white/60 text-sm mb-4">
                          选择一名队员反转其投票
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {selectedTeam.map((playerIdx) => (
                            <button
                              key={playerIdx}
                              onClick={() => handleFlipPlayerSelect(playerIdx)}
                              className="py-3 rounded-xl bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 text-white font-semibold hover:bg-purple-500/30 transition-all"
                            >
                              {playerIdx + 1}号
                            </button>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowExcaliburSelectDialog(false);
                          processQuestResult(pendingTeamVotes, null);
                        }}
                        className="w-full py-3 rounded-xl bg-avalon-card text-white border border-white/20 hover:bg-opacity-80 transition-colors"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}

                {showExcaliburFinalConfirm && pendingFlipPlayer !== null && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <div className="bg-avalon-card rounded-2xl p-6 max-w-xs w-full border border-white/10">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                          <Crown className="w-8 h-8 text-purple-400" strokeWidth={1.5} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">确认使用王者之剑？</h3>
                        <p className="text-white/60 text-sm mb-3">
                          第{voteCurrentRound}轮 · 反转{pendingFlipPlayer + 1}号玩家的投票
                        </p>
                        <p className="text-purple-400 text-xs font-semibold">
                          ✓ 成功 ↔ ✗ 失败
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={cancelExcaliburUse}
                          className="flex-1 py-3 rounded-xl bg-avalon-card text-white border border-white/20 hover:bg-opacity-80 transition-colors"
                        >
                          返回修改
                        </button>
                        <button
                          onClick={confirmExcaliburUse}
                          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold shadow-lg shadow-purple-500/20 hover:scale-105 transition-all"
                        >
                          确认反转
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showRoleConfigModal && (
          <RoleConfigModal
            isOpen={showRoleConfigModal}
            onClose={() => setShowRoleConfigModal(false)}
            playerCount={playerCount}
            roleConfigs={roleConfigs}
            onSave={saveRoleConfigs}
          />
        )}

        {showResetConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-avalon-card rounded-2xl p-6 max-w-sm w-full border border-white/10">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                  <RotateCcw className="w-8 h-8 text-red-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">确认重置游戏？</h3>
                <p className="text-white/60 text-sm">这将清空所有配置和身份发放状态</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="flex-1 py-3 rounded-xl bg-avalon-card text-white border border-white/20 hover:bg-opacity-80 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    resetGame();
                    grResetGame();
                    setShowResetConfirm(false);
                  }}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-semibold shadow-lg shadow-red-500/20 hover:scale-105 transition-all"
                >
                  确认重置
                </button>
              </div>
            </div>
          </div>
        )}

        {showViewConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-avalon-card rounded-2xl p-6 max-w-sm w-full border border-white/10">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
                  <Eye className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">查看玩家 {pendingViewPlayer} 的身份？</h3>
                <p className="text-white/60 text-sm">请确认是本人查看</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowViewConfirm(false);
                    setPendingViewPlayer(null);
                  }}
                  className="flex-1 py-3 rounded-xl bg-avalon-card text-white border border-white/20 hover:bg-opacity-80 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={confirmViewRole}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold shadow-lg shadow-blue-500/20 hover:scale-105 transition-all"
                >
                  确认查看
                </button>
              </div>
            </div>
          </div>
        )}

        {showRevealConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-avalon-card rounded-2xl p-6 max-w-sm w-full border border-white/10">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                  <Crown className="w-8 h-8 text-amber-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">揭示所有身份？</h3>
                <p className="text-white/60 text-sm">这将向所有玩家展示彼此的身份</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRevealConfirm(false)}
                  className="flex-1 py-3 rounded-xl bg-avalon-card text-white border border-white/20 hover:bg-opacity-80 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={confirmRevealAll}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold shadow-lg shadow-amber-500/20 hover:scale-105 transition-all"
                >
                  确认揭示
                </button>
              </div>
            </div>
          </div>
        )}

        {showViewModal && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            {!cardFlipped ? (
              <div className="flex flex-col items-center">
                <div
                  onClick={handleCardFlip}
                  className="relative w-64 h-96 bg-gradient-to-br from-indigo-900/80 to-purple-900/80 rounded-2xl shadow-2xl border-4 border-yellow-500 flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-transform mb-4"
                >
                  <Crown className="w-16 h-16 text-amber-400 mb-3" strokeWidth={1.5} />
                  <div className="text-center text-yellow-400">
                    <div className="font-bold text-2xl mb-2">阿瓦隆</div>
                    <div className="text-white/60 text-sm">点击翻开卡牌</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-8 py-3 bg-avalon-card rounded-xl text-white hover:bg-opacity-80 border border-white/20"
                >
                  关闭
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className={`relative w-64 h-96 rounded-2xl shadow-2xl border-4 border-yellow-500 overflow-hidden mb-4 ${
                  getPlayerRole(currentPlayer)?.faction === 'good'
                    ? 'bg-blue-500/30'
                    : 'bg-red-500/30'
                }`}>
                  {/* 顶部阵营条 */}
                  <div className={`absolute top-0 left-0 right-0 py-2 text-center font-bold text-sm ${
                    getPlayerRole(currentPlayer)?.faction === 'good' 
                      ? 'bg-blue-500/80 text-white' 
                      : 'bg-red-500/80 text-white'
                  }`}>
                    {getPlayerRole(currentPlayer)?.faction === 'good' ? '正义阵营' : '邪恶阵营'}
                  </div>
                  
                  {/* 卡牌内容 */}
                  <div className="flex flex-col items-center justify-center h-full pt-12 px-6">
                    <div className="text-7xl mb-6">
                      {getPlayerRole(currentPlayer) ? ROLES[getPlayerRole(currentPlayer)!.id].icon : '⚔️'}
                    </div>
                    
                    <div className="text-2xl font-bold text-white mb-3">
                      {players.find(p => p.id === currentPlayer)?.roleName || '忠臣'}
                    </div>
                    
                    <div className="text-white/80 text-base text-center leading-relaxed">
                      {getPlayerRole(currentPlayer) 
                        ? ROLES[getPlayerRole(currentPlayer)!.id].description 
                        : '亚瑟王的忠臣'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-8 py-3 bg-avalon-card rounded-xl text-white hover:bg-opacity-80 border border-white/20"
                >
                  我知道了
                </button>
              </div>
            )}
          </div>
        )}

        {showRoleDistributionModal && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRoleDistributionModal(false)}
          >
            <div
              className="bg-avalon-card rounded-xl p-5 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">身份发放</h3>
                <button
                  onClick={() => setShowRoleDistributionModal(false)}
                  className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-center py-8">
                <p className="text-avalon-text text-sm">随机分配身份功能</p>
              </div>
            </div>
          </div>
        )}

        {showVoteHelperModal && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowVoteHelperModal(false)}
          >
            <div
              className="bg-avalon-card rounded-xl p-5 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">投票辅助</h3>
                <button
                  onClick={() => setShowVoteHelperModal(false)}
                  className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-center py-8">
                <p className="text-avalon-text text-sm">投票辅助功能</p>
              </div>
            </div>
          </div>
        )}

        {/* 倒计时浮窗 */}
        <FloatingTimer isVisible={showFloatingTimer} onClose={() => setShowFloatingTimer(false)} />

        {/* 游戏笔记面板 */}
        {isNotePanelOpen && (
          <div
            ref={notePanelRef}
            className="fixed bg-white/95 rounded-lg shadow-xl z-[60] flex flex-col overflow-hidden"
            style={{
              right: `${notePosition.x}px`,
              top: `${notePosition.y}px`,
              width: '340px',
              height: '450px',
            }}
            onMouseDown={handleNoteMouseDown}
          >
            {/* 顶部标题栏 - 可拖拽区域 */}
            <div
              className="note-drag-handle flex items-center justify-between px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white cursor-move select-none"
              onMouseDown={handleNoteMouseDown}
            >
              <div className="flex items-center gap-2">
                <StickyNote className="w-4 h-4" />
                <span className="text-sm font-medium">游戏笔记</span>
              </div>
              <button
                onClick={() => setIsNotePanelOpen(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* 标签页切换 */}
            <div className="flex border-b border-gray-200 overflow-x-auto">
              <button
                onClick={() => setActiveNoteTab('public')}
                className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                  activeNoteTab === 'public'
                    ? 'bg-amber-100 text-amber-700 border-b-2 border-amber-500'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                公共
              </button>
              {players.map((_player, index) => (
                <button
                  key={index}
                  onClick={() => setActiveNoteTab(`player_${index}`)}
                  className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                    activeNoteTab === `player_${index}`
                      ? 'bg-amber-100 text-amber-700 border-b-2 border-amber-500'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {getPlayerTabName(index)}
                </button>
              ))}
            </div>

            {/* 笔记内容区域 */}
            <textarea
              value={getCurrentNoteContent()}
              onChange={(e) => updateNoteContent(e.target.value)}
              placeholder={activeNoteTab === 'public' ? '记录全局游戏信息...' : `记录针对 ${getPlayerTabName(parseInt(activeNoteTab.replace('player_', '')) || 0)} 的笔记...`}
              className="flex-1 p-3 text-sm text-gray-800 bg-transparent resize-none outline-none leading-relaxed"
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            />

            {/* 底部操作栏 */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200">
              <span className="text-xs text-gray-500">
                {getCurrentNoteContent().length} 字
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const newNotes = { ...playerNotes } as any;
                    newNotes[activeNoteTab] = '';
                    setPlayerNotes(newNotes);
                  }}
                  className="px-2 py-1 text-xs text-gray-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  清空当前
                </button>
                <button
                  onClick={() => {
                    const allNotes = Object.entries(playerNotes)
                      .map(([key, content]) => {
                        if (!content) return '';
                        const title = key === 'public' ? '【公共笔记】' : `【${getPlayerTabName(parseInt(key.replace('player_', '')) || 0)}】`;
                        return `${title}\n${content}`;
                      })
                      .filter(Boolean)
                      .join('\n\n---\n\n');
                    if (!allNotes) return;
                    const blob = new Blob([allNotes], { type: 'text/plain;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `阿瓦隆笔记_${new Date().toLocaleDateString()}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-2 py-1 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded transition-colors"
                >
                  导出全部
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ThemedBackground>
  );
}
